import React, { useEffect, useState, useCallback } from "react";
import { Card, Table, Button, Form, Spinner } from "react-bootstrap";
import { Modal } from "react-bootstrap";
import { FaEdit, FaTrash, FaClock, FaUtensils, FaToggleOn, FaStore, FaSearch, FaFilter, FaMapMarkerAlt, FaExclamationCircle, FaExclamationTriangle, FaRedo, FaCircle, FaRoute, FaStoreSlash, FaListUl, FaPlus, FaBoxOpen, FaTimes, FaChevronDown, FaChevronRight, FaTag, FaCopy, FaRobot, FaKey, FaFileImport, FaImage, FaEye, FaEyeSlash } from "react-icons/fa";
import { FaCodeBranch } from "react-icons/fa6";
import { Divider, Switch } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  fetchRestaurants,
  updateRestaurant,
  deleteRestaurant,
  attachStore,
} from "../actions/restaurantActions.js";
import EditRestaurantForm from "./EditRestaurantForm.js";
import restaurantPlaceholderImage from "../assets/img/restaurantimage_unavailable.png";
// Fork, plate and knife on a plain near-white field: the restaurant
// placeholder for a dish that has not been photographed yet.
import menuPlaceholder from "../assets/img/restaurant-dish-placeholder.png";
import { getUserFromLocalStorage } from "utilities/user.js";
import { usePermissions } from "../utilities/usePermissions.js";
import usePlacesAutocomplete, {
  getDetails,
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";

import {
  updateOutlet,
  deleteOutlet,
  toggleOutletActivation,
} from "../actions/outletActions.js";
import { useGoogleMaps } from "../GoogleMapsContext";
import { toast } from "react-toastify";
import { notify } from "../utilities/notify";
import LocationAutocomplete from "./LocationAutoComplete.js";
import OpenHoursManager from "../components/OpenHours/OpenHoursManager";
import SendVendorCredentialsAction from "../components/VendorCredentials/SendVendorCredentialsAction";
// The same dish picker the vendor's own Outlets tab uses; an outlet's menu
// used to be settable here only one chip at a time inside View Menu.
import OutletMenuItemsModal from "../components/OutletMenuItemsModal";
// Super user only: open the vendor's own dashboard as its owner.
import SignInAsVendorButton from "../components/SignInAsVendorButton";
import MenuImportModal from "./Components/MenuImportModal";
import {
  apiUpdateOpenHours,
  apiGetOpenStatus,
  apiFetchRestaurantSections,
  apiAdminCreateSection,
  apiAdminUpdateSection,
  apiAdminDeleteSection,
  apiAdminDeleteProduct,
  apiAdminBulkDeleteMenu,
  apiSetCatalogItemStatusBulk,
  apiUpdateOutletMenuItems,
  apiToggleRestaurantAIShoppable,
  apiSetRestaurantPublishStatus,
} from "../api/index";
import AppSelect from "../components/ui/AppSelect";

// Branded initials avatar for vendors with no logo — replaces the generic
// "image not available" stamp. Hue is derived from the name so each vendor
// gets a stable, distinct tile.
const logoInitials = (name = "") =>
  String(name).trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "R";
const LOGO_HUES = [16, 96, 152, 200, 262, 330];
const logoHue = (name = "") =>
  LOGO_HUES[[...String(name)].reduce((a, c) => a + c.charCodeAt(0), 0) % LOGO_HUES.length];
const LogoAvatar = ({ name, imageUrl, size = 56, radius = 10, fontSize = 18 }) => {
  // Either/or, never layered: many vendor logos are transparent PNGs, and a
  // gradient+initials base layer would bleed through them. The image sits on
  // white; initials render only when there is no usable image.
  const [failed, setFailed] = useState(false);
  const hue = logoHue(name);
  const showImage = imageUrl && !failed;
  return (
    <div
      title={name}
      style={{
        width: size, height: size, borderRadius: radius, overflow: "hidden", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "2px solid #e9ecef",
        background: showImage
          ? "#fff"
          : `linear-gradient(135deg, hsl(${hue},55%,46%), hsl(${hue},60%,32%))`,
      }}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => setFailed(true)}
        />
      ) : (
        <span style={{ color: "#fff", fontWeight: 800, fontSize, letterSpacing: "0.02em", userSelect: "none" }}>
          {logoInitials(name)}
        </span>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  switchBase: {
    color: theme.palette.secondary.main,
    "&$checked": {
      color: theme.palette.secondary.main,
    },
    "&$checked + $track": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  checked: {},
  track: {},
}));

const Restaurants = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { loading, restaurants, error } = useSelector(
    (state) => state.restaurants
  );
  const { mapsApiLoaded } = useGoogleMaps();
  const { can, loaded: permissionsLoaded } = usePermissions();

  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  function useCustomPlacesAutocomplete(mapsApiLoaded, retry) {
    const options = {
      requestOptions: {
        componentRestrictions: { country: "ug" },
        cacheTimeout: 10 * 60 * 60 * 24,
        debounce: 100,
        location: { lat: () => 0.347596, lng: () => 32.58252 },
        radius: 200 * 1000,
        origin: { lat: 0.3148136, lng: 32.5788721 },
        strictBounds: true,
        types: ["establishment"],
      },
      debounce: 100,
    };

    const {
      value,
      ready,
      suggestions: { data, loading: placesLoading, status: suggestionStatus } = { data: [] },
      setValue,
      clearSuggestions,
      status,
    } = usePlacesAutocomplete(options);

    useEffect(() => {
      if (mapsApiLoaded) {
        setValue("");
      }
    }, [mapsApiLoaded, retry]);

    useEffect(() => {
      if (status === "ERROR") {
        toast.error("Failed to load Google Maps API", status);
        console.error("Error fetching suggestions:", status);
      }
    }, [status]);

    return {
      value,
      ready,
      data,
      placesLoading,
      suggestionStatus,
      setValue,
      clearSuggestions,
    };
  }

  const { value, ready, data, placesLoading, suggestionStatus, setValue, clearSuggestions } =
    useCustomPlacesAutocomplete(mapsApiLoaded);

  // Hook for loading suggestions
  useEffect(() => {
    if (data && data.length > 0) {
      setLoadingSuggestions(false);
    }
  }, [data]);

  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [showAttachStoreModal, setShowAttachStoreModal] = useState(false);
  const [storeDetails, setStoreDetails] = useState({
    name: "",
    location: "",
    latitude: null,
    longitude: null,
    deliveryZoneRadius: 5,
  });
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [showEditOutletModal, setShowEditOutletModal] = useState(false);
  const [showDeleteOutletModal, setShowDeleteOutletModal] = useState(false);
  const [outletToDelete, setOutletToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toggleLoading, setToggleLoading] = useState({});
  // Which rows have their outlet list expanded under them (desktop table).
  const [expandedOutletRows, setExpandedOutletRows] = useState({});
  const [toggleOutletLoading, setToggleOutletLoading] = useState({});
  // { type: "restaurant" | "outlet", id, name, currentStatus }
  const [toggleConfirm, setToggleConfirm] = useState(null);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [selectedRestaurantForHours, setSelectedRestaurantForHours] =
    useState(null);
  const [restaurantHours, setRestaurantHours] = useState(null);
  const [isLoadingHours, setIsLoadingHours] = useState(false);
  const [isSavingHours, setIsSavingHours] = useState(false);

  // ── Menu Management Panel state ──────────────────────────────────────────
  const [showMenuPanel, setShowMenuPanel] = useState(false);
  const [menuRestaurant, setMenuRestaurant] = useState(null);
  const [menuSections, setMenuSections] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuExpandedSections, setMenuExpandedSections] = useState({});
  const [menuSearchQuery, setMenuSearchQuery] = useState("");
  // Dishes can be created before they are photographed (bulk import, or a
  // kitchen onboarding from a printed menu). This makes the outstanding ones
  // findable so the photos can be filled in progressively instead of being
  // forgotten - the import modal points operators here by name.
  const [menuPhotolessOnly, setMenuPhotolessOnly] = useState(false);
  // "Live only", on by default: the list shows what customers can see. New
  // and imported dishes start inactive (founder policy), so a 120-dish menu
  // was mostly grey "Hidden from customers" cards and the live ones were
  // hard to find. Switch it off to review the inactive ones.
  const [menuLiveOnly, setMenuLiveOnly] = useState(true);
  const [publishSaving, setPublishSaving] = useState(false);

  // Bulk menu upload (workbook + photo files). See utilities/menuImport.js.
  const [showMenuImport, setShowMenuImport] = useState(false);

  // Add section form
  const [showAddSectionForm, setShowAddSectionForm] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSectionDesc, setNewSectionDesc] = useState("");
  const [savingSection, setSavingSection] = useState(false);

  // Edit section
  const [editingSection, setEditingSection] = useState(null);
  const [editSectionTitle, setEditSectionTitle] = useState("");
  const [editSectionDesc, setEditSectionDesc] = useState("");

  // Delete confirmation (sections / products)
  const [showMenuDeleteModal, setShowMenuDeleteModal] = useState(false);
  const [menuDeleteTarget, setMenuDeleteTarget] = useState(null); // one target, or { type: 'bulk', items: [] }
  const [menuDeleteReason, setMenuDeleteReason] = useState("");
  const [menuDeleteLoading, setMenuDeleteLoading] = useState(false);
  const [menuSelectedItems, setMenuSelectedItems] = useState({});

  // Outlet menu item availability toggling
  const [outletToggling, setOutletToggling] = useState({}); // { outletId_productId: true }
  // Outlet whose dish list is open in the picker, with its parent restaurant
  // (the picker needs the parent's sections; the outlet only holds ids).
  const [outletMenuTarget, setOutletMenuTarget] = useState(null); // { outlet, restaurant }
  // Products whose Active/Inactive flip is in flight: { productId: true }.
  const [statusSaving, setStatusSaving] = useState({});

  // AI-shoppable toggle loading state
  const [aiToggleLoading, setAiToggleLoading] = useState({});
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiModalTarget, setAiModalTarget] = useState(null); // { id, name, currentValue }

  const user = getUserFromLocalStorage();
  const adminId = user?.result?._id;
  const isSuperAdmin =
    user?.result?.userType === "LUSUKU STAFF" &&
    user?.result?.user_role === "SUPERUSER";
  const canManageRestaurants =
    isSuperAdmin ||
    (permissionsLoaded && can("restaurants:manage"));
  const canCreateRestaurants =
    canManageRestaurants ||
    (permissionsLoaded && can("restaurants:create"));
  const canUpdateRestaurants =
    canManageRestaurants ||
    (permissionsLoaded && can("restaurants:update"));
  const canDeleteRestaurants =
    canManageRestaurants ||
    (permissionsLoaded && can("restaurants:delete"));
  // Bulk deletion of menu sections and dishes is a SUPER ADMIN capability,
  // not an ordinary "restaurants:delete" permission: the checkboxes, "Select
  // visible" and "Delete selected" only render for a super user, and the
  // server's /menu/bulk-delete route refuses everyone else regardless.
  const canBulkDeleteMenu = isSuperAdmin;
  // Selecting products is also how QC activates them a batch at a time, so
  // the checkboxes render for anyone who can update restaurants; only the
  // delete action on that selection stays behind the super-admin gate.
  const canSelectMenuItems = canUpdateRestaurants || canBulkDeleteMenu;
  const canApproveRestaurants =
    canManageRestaurants ||
    (permissionsLoaded && can("restaurants:approve"));
  const canViewDirectory =
    user?.result?.userType === "LUSUKU STAFF" &&
    (isSuperAdmin || !permissionsLoaded || can("restaurants:read"));

  useEffect(() => {
    dispatch(fetchRestaurants(adminId, canViewDirectory));
  }, [dispatch, adminId, canViewDirectory]);

  const handleToggleAIShoppable = (restaurantId, restaurantName, currentValue) => {
    if (!canManageRestaurants) return;
    setAiModalTarget({ id: restaurantId, name: restaurantName, currentValue });
    setShowAIModal(true);
  };

  const confirmToggleAI = async () => {
    if (!aiModalTarget) return;
    const { id, currentValue } = aiModalTarget;
    const next = !currentValue;
    setShowAIModal(false);
    setAiToggleLoading(prev => ({ ...prev, [id]: true }));
    try {
      await apiToggleRestaurantAIShoppable(id, next);
      toast.success(`AI Commerce ${next ? 'enabled' : 'disabled'} for this restaurant.`);
      dispatch(fetchRestaurants(adminId, canViewDirectory));
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${next ? 'enable' : 'disable'} AI Commerce.`);
    } finally {
      setAiToggleLoading(prev => ({ ...prev, [id]: false }));
      setAiModalTarget(null);
    }
  };

  const handleRetry = () => {
    dispatch(fetchRestaurants(adminId, canViewDirectory));
  };

  const handleDeleteRestaurant = (id) => {
    if (canDeleteRestaurants) {
      setRestaurantToDelete(id);
      setShowDeleteModal(true);
    }
  };

  const confirmDeleteRestaurant = () => {
    if (restaurantToDelete) {
      dispatch(deleteRestaurant(restaurantToDelete));
      setShowDeleteModal(false);
    }
  };

  const handleEditClick = (restaurant) => {
    if (!canUpdateRestaurants) return;
    setEditingRestaurant(restaurant);
  };

  const handleSave = async (updatedRestaurant) => {
    if (!canUpdateRestaurants) return;
    try {
      await dispatch(updateRestaurant(updatedRestaurant._id, updatedRestaurant));
      setEditingRestaurant(null);
    } catch (err) {
      // The form stays open with the changes so they can be corrected.
      notify.fromError(err, "Could not save the restaurant");
    }
  };

  const handleCancel = () => {
    setEditingRestaurant(null);
  };

  const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal);

  const handleAttachStoreClick = (restaurant) => {
    if (!canCreateRestaurants) return;
    setSelectedRestaurant(restaurant);
    setShowAttachStoreModal(true);
  };

  const handleStoreDetailsChange = (e) => {
    const { name, value } = e.target;
    setStoreDetails((prevDetails) => ({ ...prevDetails, [name]: value }));
  };

  // NOTE: `handleLocationSelect` / `handleLocationChange` used to live here and
  // were DEAD — nothing referenced them. Both <LocationAutocomplete> instances
  // in this file pass their own inline `onLocationSelect`. handleLocationSelect
  // also called `geocodeByAddress`, which this module never imported, so had
  // anything ever wired it up it would have thrown ReferenceError on the first
  // address a user picked. Removed rather than "fixed" by adding an import for
  // a helper nothing needs. Live geocoding is `handleSelect` below, which uses
  // `getGeocode` from use-places-autocomplete.

  const handleAttachStore = async () => {
    if (!canCreateRestaurants) return;
    try {
      if (!selectedRestaurant) {
        toast.error("Please select a restaurant first");
        return;
      }

      if (
        !storeDetails.name ||
        !storeDetails.location ||
        !storeDetails.deliveryZoneRadius
      ) {
        toast.error("Please fill in all required fields");
        return;
      }

      setIsSubmitting(true);

      const payload = {
        name: storeDetails.name,
        location: storeDetails.location,
        latitude: storeDetails.latitude,
        longitude: storeDetails.longitude,
        deliveryZoneRadius: Number(storeDetails.deliveryZoneRadius),
        restaurantId: selectedRestaurant._id,
      };

      // Wait for the dispatch to complete
      await dispatch(attachStore(payload));

      // If we reach here, it means the dispatch was successful
      toast.success("Store attached successfully!");

      // Clear form
      setStoreDetails({
        name: "",
        location: "",
        latitude: null,
        longitude: null,
        deliveryZoneRadius: 5,
      });

      // Clear location input and suggestions
      setValue("");
      clearSuggestions();

      // Close modal
      setShowAttachStoreModal(false);

      // Refresh restaurants list
      dispatch(fetchRestaurants(adminId, canViewDirectory));
    } catch (error) {
      console.error("Error attaching store:", error);
      toast.error(error.message || "Error attaching store");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleAttachStoreModal = () => {
    if (showAttachStoreModal) {
      setStoreDetails({
        name: "",
        location: "",
        latitude: null,
        longitude: null,
        deliveryZoneRadius: 5,
      });
      setValue("");
      clearSuggestions();
    }
    setShowAttachStoreModal(!showAttachStoreModal);
  };

  const handleEditOutlet = (outlet) => {
    if (!canUpdateRestaurants) return;
    setEditingOutlet(outlet);
    setShowEditOutletModal(true);
  };

  const handleDeleteOutlet = (outlet) => {
    if (!canDeleteRestaurants) return;
    setOutletToDelete(outlet);
    setShowDeleteOutletModal(true);
  };

  const confirmDeleteOutlet = () => {
    if (outletToDelete) {
      // The action toasts the outcome; the list keeps the outlet on a refusal.
      dispatch(deleteOutlet(outletToDelete._id, { vertical: "restaurant" })).catch(() => {});
      setShowDeleteOutletModal(false);
      setOutletToDelete(null);
    }
  };

  const handleUpdateOutlet = (updatedData) => {
    dispatch(updateOutlet(editingOutlet._id, updatedData, { vertical: "restaurant" })).catch(() => {});
    setShowEditOutletModal(false);
    setEditingOutlet(null);
  };

  const handleToggleActive = (restaurantId, currentStatus, restaurantName) => {
    if (!canApproveRestaurants) return;
    setToggleConfirm({
      type: "restaurant",
      id: restaurantId,
      name: restaurantName || "this restaurant",
      currentStatus,
    });
  };

  const handleToggleOutletActive = (outletId, currentStatus, outletName) => {
    if (!canApproveRestaurants) return;
    setToggleConfirm({
      type: "outlet",
      id: outletId,
      name: outletName || "this outlet",
      currentStatus,
    });
  };

  const performToggle = async () => {
    if (!toggleConfirm) return;
    const { type, id, currentStatus } = toggleConfirm;
    const action = currentStatus ? "deactivate" : "activate";
    setToggleConfirm(null);

    if (type === "restaurant") {
      setToggleLoading((prev) => ({ ...prev, [id]: true }));
      try {
        await dispatch(updateRestaurant(id, { isActive: !currentStatus }));
        toast.success(`Restaurant ${action}d successfully!`);
        // Refresh restaurants list
        dispatch(fetchRestaurants(adminId, canViewDirectory));
      } catch (error) {
        notify.fromError(error, `Could not ${action} the restaurant`);
      } finally {
        setToggleLoading((prev) => ({ ...prev, [id]: false }));
      }
      return;
    }

    setToggleOutletLoading((prev) => ({ ...prev, [id]: true }));
    try {
      // The action toasts the outcome once.
      await dispatch(toggleOutletActivation(id, !currentStatus, { vertical: "restaurant" }));
      // Refresh restaurants list
      dispatch(fetchRestaurants(adminId, canViewDirectory));
    } catch (error) {
      console.error(`Failed to ${action} outlet:`, error?.message);
    } finally {
      setToggleOutletLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleSelect = async (address) => {
    console.log("Address selected:", address);
    setValue(address, false);
    clearSuggestions();

    try {
      // First get the geocode results
      const results = await getGeocode({
        address,
        componentRestrictions: { country: "ug" },
        region: "ug",
      });

      // console.log("Geocode results:", results);

      if (results && results.length > 0) {
        // Get the lat/lng
        const { lat, lng } = await getLatLng(results[0]);
        // console.log("Location coordinates:", { lat, lng });

        // Get additional place details
        const placeId = results[0].place_id;
        // console.log("Place ID:", placeId);

        setStoreDetails((prev) => ({
          ...prev,
          location: address,
          latitude: lat,
          longitude: lng,
          place_id: placeId,
        }));
      } else {
        throw new Error("No results found");
      }
    } catch (error) {
      console.error("Geocoding error details:", error);
      toast.error(
        "Error getting location coordinates. Please try selecting a different address."
      );
    }
  };

  const handleManageHours = async (restaurant) => {
    if (!canUpdateRestaurants) return;
    setSelectedRestaurantForHours(restaurant);
    setShowHoursModal(true);
    setIsLoadingHours(true);

    try {
      const response = await apiGetOpenStatus(restaurant._id);
      setRestaurantHours(response.data.openHours || null);
    } catch (error) {
      console.error("Error fetching restaurant hours:", error);
      toast.error("Failed to load restaurant hours");
      setRestaurantHours(null);
    } finally {
      setIsLoadingHours(false);
    }
  };

  const handleSaveRestaurantHours = async (hoursData, timezone) => {
    if (!selectedRestaurantForHours || !canUpdateRestaurants) return;

    setIsSavingHours(true);
    try {
      await apiUpdateOpenHours(selectedRestaurantForHours._id, {
        openHours: hoursData,
        ...(timezone ? { timezone } : {}),
      });

      toast.success(
        `Business hours updated for ${selectedRestaurantForHours.name}!`
      );
      setShowHoursModal(false);
      // Refresh restaurants list
      dispatch(fetchRestaurants(adminId, canViewDirectory));
    } catch (error) {
      console.error("Error saving restaurant hours:", error);
      toast.error("Failed to update business hours");
    } finally {
      setIsSavingHours(false);
    }
  };

  // ── Menu Panel handlers ──────────────────────────────────────────────────
  const loadMenuSections = useCallback(async (restaurant) => {
    setMenuLoading(true);
    try {
      const res = await apiFetchRestaurantSections(restaurant._id);
      const sections = res.data || [];
      setMenuSections(sections);
      // Expand all sections by default
      const expanded = {};
      sections.forEach((s) => { expanded[s._id] = true; });
      setMenuExpandedSections(expanded);
      setMenuSelectedItems({});
    } catch (err) {
      toast.error("Failed to load menu sections");
      setMenuSections([]);
    } finally {
      setMenuLoading(false);
    }
  }, []);

  const handleOpenMenuPanel = (restaurant) => {
    setMenuRestaurant(restaurant);
    setShowMenuPanel(true);
    setMenuSearchQuery("");
    setShowAddSectionForm(false);
    setEditingSection(null);
    setMenuSelectedItems({});
    loadMenuSections(restaurant);
  };

  // Publishing is what makes a kitchen visible to customers. It is deliberately
  // NOT the same control as Active: a draft has never been live, a deactivated
  // restaurant was live and is suspended.
  const handleTogglePublish = async () => {
    if (!menuRestaurant?._id || publishSaving) return;
    const publish = menuRestaurant.publishStatus === "DRAFT";
    setPublishSaving(true);
    try {
      const { data } = await apiSetRestaurantPublishStatus(menuRestaurant._id, publish);
      const nextStatus = data?.restaurant?.publishStatus || (publish ? "PUBLISHED" : "DRAFT");
      setMenuRestaurant((prev) => (prev ? { ...prev, publishStatus: nextStatus } : prev));
      // The directory behind the drawer is redux-backed; refetch it the way
      // every other mutation on this screen does.
      dispatch(fetchRestaurants(adminId, canViewDirectory));
      toast.success(data?.message || (publish ? "Restaurant published." : "Restaurant unpublished."));
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          `Failed to ${publish ? "publish" : "unpublish"} this restaurant.`
      );
    }
    setPublishSaving(false);
  };

  const handleCloseMenuPanel = () => {
    setShowMenuPanel(false);
    setMenuRestaurant(null);
    setMenuSections([]);
    setEditingSection(null);
    setShowAddSectionForm(false);
    setOutletToggling({});
    setMenuSelectedItems({});
    setMenuDeleteTarget(null);
    setMenuDeleteReason("");
  };

  // Save the picker's selection for one outlet, then refresh the directory
  // so the card's count and the View Menu chips agree with the server.
  const handleSaveOutletMenuItems = async (menuItemIds) => {
    if (!outletMenuTarget) return;
    const { outlet } = outletMenuTarget;
    try {
      await apiUpdateOutletMenuItems(outlet._id, menuItemIds);
      toast.success(`${outlet.name} now serves ${menuItemIds.length} dish${menuItemIds.length === 1 ? "" : "es"}`);
      dispatch(fetchRestaurants(adminId, canViewDirectory));
    } catch (err) {
      toast.error(`Failed to update ${outlet.name} menu`);
      throw err;
    }
  };

  const handleToggleOutletProduct = async (outlet, product) => {
    if (!canUpdateRestaurants) return;
    const key = `${outlet._id}_${product._id}`;
    if (outletToggling[key]) return;
    setOutletToggling((prev) => ({ ...prev, [key]: true }));
    const currentItems = outlet.menuItems || [];
    const productIdStr = product._id?.toString();
    const isCurrentlyEnabled = currentItems.some(
      (id) => id?.toString() === productIdStr
    );
    const updatedItems = isCurrentlyEnabled
      ? currentItems.filter((id) => id?.toString() !== productIdStr)
      : [...currentItems, product._id];
    try {
      await apiUpdateOutletMenuItems(outlet._id, updatedItems);
      // Update outlet.menuItems in local menuRestaurant state
      setMenuRestaurant((prev) => ({
        ...prev,
        outlets: prev.outlets.map((o) =>
          o._id === outlet._id ? { ...o, menuItems: updatedItems } : o
        ),
      }));
      toast.success(
        isCurrentlyEnabled
          ? `Removed from ${outlet.name}`
          : `Added to ${outlet.name}`
      );
    } catch (err) {
      toast.error(`Failed to update ${outlet.name} menu`);
    } finally {
      setOutletToggling((prev) => ({ ...prev, [key]: false }));
    }
  };

  const toggleSectionExpand = (sectionId) => {
    setMenuExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!canCreateRestaurants) return;
    if (!newSectionTitle.trim()) { toast.error("Section title is required"); return; }
    setSavingSection(true);
    try {
      await apiAdminCreateSection(menuRestaurant._id, {
        title: newSectionTitle.trim(),
        description: newSectionDesc.trim(),
        restaurantId: menuRestaurant._id,
      });
      toast.success("Section created!");
      setNewSectionTitle("");
      setNewSectionDesc("");
      setShowAddSectionForm(false);
      await loadMenuSections(menuRestaurant);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create section");
    } finally {
      setSavingSection(false);
    }
  };

  const handleStartEditSection = (section) => {
    if (!canUpdateRestaurants) return;
    setEditingSection(section._id);
    setEditSectionTitle(section.title);
    setEditSectionDesc(section.description || "");
  };

  const handleSaveEditSection = async (sectionId) => {
    if (!canUpdateRestaurants) return;
    if (!editSectionTitle.trim()) { toast.error("Title required"); return; }
    setSavingSection(true);
    try {
      await apiAdminUpdateSection(sectionId, {
        title: editSectionTitle.trim(),
        description: editSectionDesc.trim(),
      });
      toast.success("Section updated!");
      setEditingSection(null);
      await loadMenuSections(menuRestaurant);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update section");
    } finally {
      setSavingSection(false);
    }
  };

  /* Activate or deactivate dishes. ONE call site for the card pill and the
     "Activate selected / Deactivate selected" buttons: every new or imported
     dish starts Inactive and this is how QC turns it on once it has been
     checked. The server flips each dish it is allowed to and reports the
     rest; the local list is patched from what it says actually changed. */
  const setMenuProductsEnabled = async (productIds, enabled) => {
    const ids = Array.from(new Set(productIds.map(String))).filter((id) => !statusSaving[id]);
    if (!ids.length || !canUpdateRestaurants) return;
    setStatusSaving((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, true])) }));
    try {
      const { data } = await apiSetCatalogItemStatusBulk("restaurant", ids, enabled);
      const result = data?.data || {};
      const flipped = new Set((result.changed || []).map((e) => String(e.productId)));
      if (flipped.size) {
        setMenuSections((prev) =>
          prev.map((section) => ({
            ...section,
            products: (section.products || []).map((p) =>
              flipped.has(String(p._id)) ? { ...p, enabled } : p
            ),
          }))
        );
      }
      const problems = (result.forbidden?.length || 0) + (result.notFound?.length || 0);
      (problems ? toast.warn : toast.success)(data?.message || (enabled ? "Activated." : "Deactivated."));
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${enabled ? "activate" : "deactivate"}.`);
    } finally {
      setStatusSaving((prev) => {
        const next = { ...prev };
        ids.forEach((id) => delete next[id]);
        return next;
      });
    }
  };

  const menuTargetKey = (target) => `${target.type}:${target.id}`;

  const openMenuDelete = (target) => {
    setMenuDeleteTarget(target);
    setMenuDeleteReason("");
    setShowMenuDeleteModal(true);
  };

  const toggleMenuSelection = (target) => {
    const key = menuTargetKey(target);
    setMenuSelectedItems((current) => {
      const next = { ...current };
      if (next[key]) delete next[key];
      else next[key] = target;
      return next;
    });
  };

  // ONE predicate for the toolbar filters (search, no photo, live only),
  // shared by the selection helpers and the rendered list so "Select visible"
  // can never select something the list is not showing.
  const menuProductMatches = (product) => {
    const q = menuSearchQuery.trim().toLowerCase();
    if (
      q &&
      !product.name?.toLowerCase().includes(q) &&
      !product.description?.toLowerCase().includes(q)
    ) {
      return false;
    }
    if (menuPhotolessOnly && product.image) return false;
    if (menuLiveOnly && product.enabled === false) return false;
    return true;
  };
  const menuFiltersActive = Boolean(
    menuSearchQuery.trim() || menuPhotolessOnly || menuLiveOnly
  );
  const inactiveMenuProductCount = menuSections.reduce(
    (n, sec) => n + (sec.products || []).filter((p) => p.enabled === false).length,
    0
  );

  const visibleMenuProducts = menuSections.flatMap((section) =>
    (section.products || [])
      .filter(menuProductMatches)
      .map((product) => ({
        type: "product",
        id: product._id,
        name: product.name,
        sectionId: section._id,
      }))
  );
  const selectedMenuTargets = Object.values(menuSelectedItems);
  // Dishes covered by the selection: ticked dishes plus every dish inside a
  // ticked section. This is what Activate/Deactivate selected acts on.
  const selectedMenuProductIds = Array.from(
    new Set(
      selectedMenuTargets.flatMap((target) => {
        if (target.type === "product") return [String(target.id)];
        const section = menuSections.find((s) => String(s._id) === String(target.id));
        return (section?.products || []).map((p) => String(p._id));
      })
    )
  );
  const allVisibleProductsSelected =
    visibleMenuProducts.length > 0 &&
    visibleMenuProducts.every((target) => menuSelectedItems[menuTargetKey(target)]);

  const toggleVisibleProductSelection = () => {
    setMenuSelectedItems((current) => {
      const next = { ...current };
      for (const target of visibleMenuProducts) {
        const key = menuTargetKey(target);
        if (allVisibleProductsSelected) delete next[key];
        else next[key] = target;
      }
      return next;
    });
  };

  const closeMenuDeleteModal = () => {
    if (menuDeleteLoading) return;
    setShowMenuDeleteModal(false);
    setMenuDeleteTarget(null);
    setMenuDeleteReason("");
  };

  const handleMenuDeleteConfirm = async () => {
    if (!menuDeleteTarget || !canDeleteRestaurants) return;
    const reason = menuDeleteReason.trim();
    if (reason.length < 3) {
      toast.error("Please enter a deletion reason (at least 3 characters).");
      return;
    }

    const requestedTargets =
      menuDeleteTarget.type === "bulk"
        ? menuDeleteTarget.items || []
        : [menuDeleteTarget];
    const selectedSectionIds = new Set(
      requestedTargets
        .filter((target) => target.type === "section")
        .map((target) => target.id)
    );
    // A selected section already deletes its children. Do not send redundant
    // product requests that would race the cascade and produce false 404s.
    const targets = requestedTargets.filter(
      (target) =>
        target.type === "section" || !selectedSectionIds.has(target.sectionId)
    );

    setMenuDeleteLoading(true);
    try {
      if (menuDeleteTarget.type === "bulk") {
        // One request, one super-admin gate, one audit row per entity on the
        // server. Never loop the single-item endpoints for a bulk delete: that
        // hid a super-admin capability behind vendor-scoped guards.
        if (!canBulkDeleteMenu) {
          toast.error("Only a super admin can delete several menu items at once.");
          return;
        }
        const { data } = await apiAdminBulkDeleteMenu({
          sectionIds: targets.filter((t) => t.type === "section").map((t) => t.id),
          productIds: targets.filter((t) => t.type === "product").map((t) => t.id),
          reason,
        });
        const summary = data?.data?.deleted || { sections: 0, products: 0 };
        const missing =
          (data?.data?.notFound?.sections?.length || 0) +
          (data?.data?.notFound?.products?.length || 0);
        if (summary.sections + summary.products > 0) {
          toast.success(data?.message || "Menu items deleted");
        }
        if (missing > 0) {
          toast.info(`${missing} selected item${missing === 1 ? " was" : "s were"} already gone.`);
        }
      } else {
        const single =
          menuDeleteTarget.type === "section"
            ? apiAdminDeleteSection(menuDeleteTarget.id, reason)
            : apiAdminDeleteProduct(menuDeleteTarget.id, reason);
        await single;
        toast.success("Menu item deleted");
      }
      setShowMenuDeleteModal(false);
      setMenuDeleteTarget(null);
      setMenuDeleteReason("");
      await loadMenuSections(menuRestaurant);
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setMenuDeleteLoading(false);
    }
  };

  // What the dish forms need to show the restaurant immediately, without a
  // round trip; they still confirm it from the server (WorkingRestaurant.js).
  const menuRestaurantHint = () =>
    menuRestaurant
      ? {
          name: menuRestaurant.name,
          ownerId: menuRestaurant.owner ? String(menuRestaurant.owner) : undefined,
          imageUrl: menuRestaurant.imageUrl || null,
          commissionRate: menuRestaurant.commissionRate,
          commissionConfigured: Boolean(menuRestaurant.commissionConfigured),
        }
      : null;

  const handleAddProductToSection = (sectionId) => {
    if (!canCreateRestaurants) return;
    history.push({
      pathname: "/admin/menu-create",
      state: { sectionId, restaurantId: menuRestaurant._id, restaurantHint: menuRestaurantHint() },
    });
  };

  const handleEditProduct = (productId) => {
    if (!canUpdateRestaurants) return;
    history.push({
      pathname: `/admin/edit-menu-product/${productId}`,
      state: { restaurantHint: menuRestaurantHint() },
    });
  };

  const modalDeleteTargets = menuDeleteTarget
    ? menuDeleteTarget.type === "bulk"
      ? menuDeleteTarget.items || []
      : [menuDeleteTarget]
    : [];
  const modalSectionTargets = modalDeleteTargets.filter(
    (target) => target.type === "section"
  );
  const modalSectionIds = new Set(modalSectionTargets.map((target) => target.id));
  const modalDirectProductTargets = modalDeleteTargets.filter(
    (target) =>
      target.type === "product" && !modalSectionIds.has(target.sectionId)
  );
  const modalAffectedProductCount =
    modalDirectProductTargets.length +
    modalSectionTargets.reduce(
      (count, section) => count + (section.productCount || 0),
      0
    );

  // ── KPI counts ──────────────────────────────────────────────────────────
  const totalRestaurants  = restaurants?.length || 0;
  const activeRestaurants = restaurants?.filter((r) => r.isActive).length || 0;
  const totalOutlets      = restaurants?.reduce((s, r) => s + (r.outlets?.length || 0), 0) || 0;
  const hoursConfigured   = restaurants?.filter((r) => r.openHours).length || 0;

  // ── Filter state ────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery]       = useState("");
  const [statusFilter, setStatusFilter]     = useState("all");
  const [hoursFilter, setHoursFilter]       = useState("all");
  const [outletsFilter, setOutletsFilter]   = useState("all");

  const filteredRestaurants = (restaurants || []).filter((r) => {
    const matchesSearch = !searchQuery ||
      r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.address?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && r.isActive) ||
      (statusFilter === "inactive" && !r.isActive);
    const matchesHours =
      hoursFilter === "all" ||
      (hoursFilter === "configured" && r.openHours) ||
      (hoursFilter === "not-set" && !r.openHours);
    const matchesOutlets =
      outletsFilter === "all" ||
      (outletsFilter === "with" && r.outlets?.length > 0) ||
      (outletsFilter === "without" && (!r.outlets || r.outlets.length === 0));
    return matchesSearch && matchesStatus && matchesHours && matchesOutlets;
  });

  const hasActiveFilters = searchQuery || statusFilter !== "all" || hoursFilter !== "all" || outletsFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setHoursFilter("all");
    setOutletsFilter("all");
  };

  return (
    <>
      <style>{`
        .rst-page { background: #f4f6f9; min-height: 100vh; padding: 24px 20px; font-family: "Segoe UI", system-ui, sans-serif; }

        /* KPI grid */
        .rst-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        .rst-kpi-card { background: #fff; border-radius: 10px; padding: 18px 20px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); border-top: 4px solid transparent; position: relative; overflow: hidden; }
        .rst-kpi-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; margin-bottom: 6px; }
        .rst-kpi-value { font-size: 2rem; font-weight: 800; line-height: 1; margin-bottom: 4px; }
        .rst-kpi-sub   { font-size: 0.75rem; color: #8a9ab0; }
        .rst-kpi-icon  { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); opacity: 0.09; font-size: 48px; pointer-events: none; }

        /* Filter bar */
        .rst-filter-bar { display: flex; align-items: center; gap: 10px; padding: 12px 20px; background: #f8fafc; border-bottom: 1px solid #e9ecef; flex-wrap: wrap; }
        .rst-filter-search { position: relative; flex: 1; min-width: 180px; max-width: 280px; }
        .rst-filter-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9aacbf; font-size: 13px; pointer-events: none; }
        .rst-filter-search input { width: 100%; padding: 7px 12px 7px 32px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.82rem; outline: none; background: #fff; transition: border-color 0.2s; }
        .rst-filter-search input:focus { border-color: #5bc5f5; }
        .rst-filter-select { padding: 7px 10px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.8rem; color: #4a5568; background: #fff; outline: none; cursor: pointer; transition: border-color 0.2s; }
        .rst-filter-select:focus { border-color: #5bc5f5; }
        .rst-filter-label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #8a9ab0; white-space: nowrap; }
        .rst-filter-clear { height: 33px; padding: 0 12px; border-radius: 8px; border: 1.5px solid #e4e8ef; background: #fff; color: #c62828; font-size: 0.78rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: background 0.15s; }
        .rst-filter-clear:hover { background: #fce4ec; }
        .rst-filter-results { margin-left: auto; font-size: 0.75rem; color: #8a9ab0; white-space: nowrap; }

        /* Panel */
        .rst-panel { background: #fff; border-radius: 10px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; }

        /* Dark header */
        .rst-header {
          background: linear-gradient(135deg, #1a2a3a 0%, #243447 100%);
          padding: 16px 24px; display: flex; align-items: center;
          justify-content: space-between; gap: 12px; flex-wrap: wrap;
        }
        .rst-header-title { color: #fff; font-size: 1.1rem; font-weight: 800; margin: 0; display: flex; align-items: center; gap: 8px; }
        .rst-header-title i { color: #5bc5f5; }
        .rst-header-sub { color: #8aaccc; font-size: 0.78rem; margin: 2px 0 0; }

        /* Table */
        .rst-table-wrap { overflow-x: auto; }
        table.rst-table { width: 100%; border-collapse: collapse; }
        table.rst-table thead tr { background: #f4f6f9; }
        table.rst-table thead th { padding: 10px 14px; font-size: 0.67rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; border-bottom: 2px solid #e9ecef; white-space: nowrap; border-top: none; }
        table.rst-table thead th:first-child { padding-left: 20px; }
        table.rst-table tbody tr { border-left: 3px solid transparent; transition: background 0.12s, border-color 0.12s; cursor: default; }
        table.rst-table tbody tr:hover { background: #f7fbff; border-left-color: #5bc5f5; }
        table.rst-table tbody td { padding: 12px 14px; font-size: 0.82rem; color: #2d3748; border-bottom: 1px solid #f0f2f5; vertical-align: middle; }
        table.rst-table tbody td:first-child { padding-left: 20px; }
        table.rst-table tbody tr:last-child td { border-bottom: none; }

        /* Logo */
        .rst-logo { width: 64px; height: 64px; border-radius: 12px; object-fit: cover; border: 2px solid #e9ecef; }

        /* Name cell — reserve real width so the name doesn't wrap into a tall
           multi-line stack, and clamp name + address so rows stay one height. */
        .rst-name-cell { min-width: 240px; max-width: 320px; }
        .rst-name { font-weight: 700; color: #1a2a3a; font-size: 0.88rem; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .rst-addr { font-size: 0.72rem; color: #9aacbf; margin-top: 2px; display: flex; align-items: flex-start; gap: 4px; }
        .rst-addr svg { flex-shrink: 0; margin-top: 2px; }
        .rst-addr-text { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.35; }

        /* ID pill */
        .rst-id-pill { display: inline-flex; align-items: center; gap: 5px; height: 24px; font-family: "SFMono-Regular", Consolas, monospace; font-size: 0.7rem; font-weight: 600; background: #f4f6f9; color: #6b7c93; padding: 0 10px; border-radius: 20px; border: 1px solid #e4e8ef; cursor: pointer; transition: border-color 0.15s, color 0.15s, background 0.15s; white-space: nowrap; }
        .rst-id-pill:hover { border-color: #5bc5f5; color: #1565c0; background: #eef8fe; }
        .rst-id-pill svg { font-size: 9px; opacity: 0.7; }

        /* Outlet mini-list */
        .rst-outlet-count { display: inline-flex; align-items: center; gap: 5px; background: #e8f4fd; color: #1565c0; border: 1px solid #b3d9f7; padding: 2px 10px; border-radius: 20px; font-size: 0.72rem; font-weight: 700; margin-bottom: 8px; }
        .rst-outlet-item { padding: 7px 10px; border-left: 3px solid #4169E1; border-radius: 6px; background: #f7f9fc; margin-bottom: 5px; font-size: 0.78rem; }
        .rst-outlet-name { font-weight: 700; color: #1a2a3a; display: flex; align-items: center; gap: 6px; }
        .rst-outlet-meta { font-size: 0.7rem; color: #9aacbf; display: flex; align-items: center; gap: 4px; margin-top: 2px; }
        .rst-outlet-actions { display: flex; align-items: center; gap: 6px; margin-top: 6px; }

        /* Status badge */
        .rst-status { display: inline-flex; align-items: center; gap: 5px; height: 22px; padding: 0 10px; border-radius: 20px; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.03em; white-space: nowrap; }

        /* Hours badge */
        .rst-hours-badge { display: inline-flex; align-items: center; gap: 5px; height: 22px; padding: 0 10px; border-radius: 20px; font-size: 0.7rem; font-weight: 700; cursor: pointer; transition: opacity 0.15s; white-space: nowrap; }
        .rst-hours-badge:hover { opacity: 0.8; }

        /* Action buttons */
        .rst-actions { display: flex; flex-direction: column; gap: 5px; min-width: 120px; }
        .rst-btn { height: 30px; padding: 0 12px; border-radius: 7px; border: none; display: inline-flex; align-items: center; justify-content: center; gap: 5px; font-size: 0.75rem; font-weight: 700; cursor: pointer; transition: opacity 0.15s; white-space: nowrap; width: 100%; }
        .rst-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .rst-btn-edit   { background: #4169E1; color: #fff; }
        .rst-btn-edit:hover:not(:disabled) { background: #3457c9; }
        .rst-btn-del    { background: transparent; border: 1.5px solid #dc3545; color: #dc3545; }
        .rst-btn-del:hover:not(:disabled) { background: #f8d7da; }
        .rst-btn-attach { background: #4B0082; color: #fff; }
        .rst-btn-asvendor { background: transparent; border: 1.5px solid #4B0082; color: #4B0082; }
        .rst-btn-asvendor:hover { background: #4B0082; color: #fff; }
        .rst-btn-attach:hover:not(:disabled) { background: #3a006a; }
        .rst-btn-hours  { background: #117a44; color: #fff; }
        .rst-btn-hours:hover:not(:disabled) { background: #0e6236; }
        .rst-btn-creds  { background: #023957; color: #fff; }
        .rst-btn-creds:hover:not(:disabled) { background: #012b42; }

        /* Outlet inline btn */
        .rst-outlet-btn { height: 24px; padding: 0 8px; border-radius: 5px; border: none; display: inline-flex; align-items: center; gap: 4px; font-size: 0.7rem; font-weight: 700; cursor: pointer; transition: opacity 0.15s; }
        .rst-outlet-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .rst-outlet-btn-edit { background: #e3f2fd; color: #1565c0; }
        .rst-outlet-btn-del  { background: #fce4ec; color: #c62828; }
        /* Menu Items and Edit wear the dashboard action colour (lk-action-btn in
           lusuku-overrides.css). Only the amber dot survives from the old tint:
           it marks an outlet that serves nothing yet, which is the state a
           freshly attached store sits in. */
        .rst-outlet-menu-empty { width: 7px; height: 7px; border-radius: 50%; background: #f0b429; margin-left: 2px; }

        /* Mobile card list */
        .rst-card-list { display: none; padding: 12px; }
        /* Fit-to-width desktop table: no sideways scroll on a laptop. */
        table.rst-table-fit { table-layout: fixed; }
        table.rst-table-fit tbody td { padding: 10px 12px; vertical-align: top; }
        table.rst-table-fit thead th { padding: 10px 12px; }
        .rst-ident { display: flex; align-items: flex-start; gap: 10px; min-width: 0; }
        .rst-ident-index { color: #9aacbf; font-weight: 700; font-size: 0.72rem; min-width: 18px; padding-top: 24px; }
        .rst-ident-text { min-width: 0; flex: 1; }
        .rst-id-pill-sm { height: 20px; font-size: 0.66rem; padding: 0 8px; margin-top: 5px; }
        .rst-stack { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; min-width: 0; }
        .rst-stack .rst-hours-badge, .rst-stack .mnu-stat-pill { max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
        .rst-switch-line { display: flex; align-items: center; gap: 2px; margin-left: -8px; }
        .rst-outlet-toggle { cursor: pointer; margin-bottom: 0; white-space: nowrap; }
        .rst-actions-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 5px; min-width: 0; }
        .rst-actions-grid .rst-btn { height: 28px; padding: 0 6px; font-size: 0.72rem; overflow: hidden; text-overflow: ellipsis; }
        .rst-outlet-row td { background: #f8fafc; padding: 10px 20px 14px !important; }
        .rst-outlet-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 8px; }
        .rst-mobile-card { background: #fff; border-radius: 10px; padding: 14px 16px; border: 1px solid #e9ecef; border-left: 4px solid #4169E1; margin-bottom: 12px; }
        .rst-mc-header { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
        .rst-mc-logo { width: 44px; height: 44px; border-radius: 8px; object-fit: cover; border: 1px solid #e9ecef; flex-shrink: 0; }
        .rst-mc-name { font-weight: 700; color: #1a2a3a; font-size: 0.9rem; }
        .rst-mc-addr { font-size: 0.73rem; color: #9aacbf; margin-top: 2px; }
        .rst-mc-badges { display: flex; gap: 6px; flex-wrap: wrap; margin: 8px 0; }
        .rst-mc-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; margin-top: 10px; }

        /* Empty */
        .rst-empty { text-align: center; padding: 52px 20px; }
        .rst-empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.2; color: #8a9ab0; display: flex; justify-content: center; }
        .rst-empty-msg { font-size: 0.88rem; color: #9aacbf; font-style: italic; }

        /* Error state */
        .rst-error { display: flex; align-items: center; gap: 12px; background: #fff3f4; border: 1.5px solid #f5c2c7; border-radius: 10px; padding: 16px 20px; color: #c62828; font-size: 0.9rem; font-weight: 600; margin: 20px; }

        /* Modal form fields */
        .rst-modal-label { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #6b7c93; margin-bottom: 5px; display: block; }
        .rst-modal-input { width: 100%; padding: 9px 14px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.85rem; outline: none; transition: border-color 0.2s; }
        .rst-modal-input:focus { border-color: #5bc5f5; }
        .rst-modal-footer-btn { height: 36px; padding: 0 20px; border-radius: 8px; border: none; font-size: 0.82rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }

        /* Responsive */
        @media (max-width: 900px) {
          .rst-kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .rst-kpi-icon { display: none; }
        }
        @media (max-width: 768px) {
          .rst-page { padding: 16px 12px; }
          .rst-table-wrap { display: none; }
          .rst-card-list { display: block; }
          .rst-kpi-grid { grid-template-columns: repeat(2, 1fr); }
          .rst-filter-bar { gap: 8px; padding: 10px 12px; }
          .rst-filter-search { max-width: 100%; }
          .rst-filter-results { display: none; }
        }
        @media (max-width: 480px) {
          .rst-kpi-value { font-size: 1.5rem; }
        }

        /* Menu button */
        .rst-btn-menu { background: #0e7490; color: #fff; }
        .rst-btn-menu:hover:not(:disabled) { background: #0c6480; }

        /* ── Menu Panel Overlay ── */
        .mnu-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          z-index: 1040; display: flex; justify-content: flex-end;
          animation: mnu-fade-in 0.2s ease;
        }
        @keyframes mnu-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .mnu-drawer {
          width: min(820px, 100vw); height: 100vh; background: #fff;
          display: flex; flex-direction: column; overflow: hidden;
          box-shadow: -4px 0 32px rgba(0,0,0,0.18);
          animation: mnu-slide-in 0.25s ease;
        }
        @keyframes mnu-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }

        /* Header */
        .mnu-header {
          background: linear-gradient(135deg, #1a2a3a 0%, #0e7490 100%);
          padding: 18px 24px; display: flex; align-items: center; gap: 14px; flex-shrink: 0;
        }
        .mnu-header-logo { width: 48px; height: 48px; border-radius: 10px; object-fit: cover; border: 2px solid rgba(255,255,255,0.25); flex-shrink: 0; }
        .mnu-header-info { flex: 1; min-width: 0; }
        .mnu-header-title { color: #fff; font-size: 1.1rem; font-weight: 800; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mnu-header-sub { color: rgba(255,255,255,0.7); font-size: 0.75rem; margin: 3px 0 0; }
        .mnu-close-btn { width: 36px; height: 36px; border-radius: 50%; background: rgba(255,255,255,0.15); border: none; color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: background 0.15s; }
        .mnu-close-btn:hover { background: rgba(255,255,255,0.3); }

        /* Toolbar */
        .mnu-toolbar {
          padding: 12px 20px; background: #f8fafc; border-bottom: 1px solid #e9ecef;
          display: flex; align-items: center; gap: 10px; flex-shrink: 0; flex-wrap: wrap;
        }
        .mnu-search-wrap { position: relative; flex: 1; min-width: 160px; }
        .mnu-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9aacbf; font-size: 12px; pointer-events: none; }
        .mnu-search-input { width: 100%; padding: 7px 12px 7px 30px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.82rem; outline: none; background: #fff; }
        .mnu-search-input:focus { border-color: #0e7490; }
        .mnu-add-section-btn { height: 33px; padding: 0 14px; border-radius: 8px; border: none; background: #117a44; color: #fff; font-size: 0.78rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0; transition: background 0.15s; }
        .mnu-add-section-btn:hover { background: #0e6236; }
        .mnu-stats { margin-left: auto; display: flex; gap: 12px; flex-shrink: 0; }
        .mnu-stat-pill { display: inline-flex; align-items: center; gap: 5px; height: 22px; background: #f0f4f8; color: #4a5568; border: 1px solid #e4e8ef; padding: 0 10px; border-radius: 20px; font-size: 0.7rem; font-weight: 700; white-space: nowrap; }

        /* Add section inline form */
        .mnu-add-section-form { padding: 14px 20px; background: #f0fdf4; border-bottom: 1.5px solid #bbf7d0; flex-shrink: 0; }
        .mnu-add-section-form input { width: 100%; padding: 8px 12px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.85rem; outline: none; margin-bottom: 8px; }
        .mnu-add-section-form input:focus { border-color: #117a44; }
        .mnu-form-row { display: flex; gap: 8px; }
        .mnu-form-btn { height: 32px; padding: 0 16px; border-radius: 7px; border: none; font-size: 0.78rem; font-weight: 700; cursor: pointer; }
        .mnu-form-btn-save { background: #117a44; color: #fff; }
        .mnu-form-btn-cancel { background: #f4f6f9; color: #6b7c93; border: 1.5px solid #e4e8ef; }

        /* Body */
        .mnu-body { flex: 1; overflow-y: auto; padding: 16px 20px; }

        /* Empty */
        .mnu-empty { text-align: center; padding: 60px 20px; color: #9aacbf; }
        .mnu-empty-icon { font-size: 3rem; margin-bottom: 12px; opacity: 0.3; }

        /* Section card */
        .mnu-section { border: 1.5px solid #e4e8ef; border-radius: 10px; margin-bottom: 14px; overflow: hidden; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
        .mnu-section-header { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #f8fafc; cursor: pointer; border-bottom: 1px solid #e4e8ef; transition: background 0.12s; }
        .mnu-section-header:hover { background: #f0f4f8; }
        .mnu-section-chevron { color: #9aacbf; font-size: 11px; flex-shrink: 0; transition: transform 0.2s; }
        .mnu-section-chevron.open { transform: rotate(90deg); }
        .mnu-section-name { font-weight: 700; font-size: 0.9rem; color: #1a2a3a; flex: 1; }
        .mnu-section-count { display: inline-flex; align-items: center; gap: 4px; background: #e8f4fd; color: #1565c0; border: 1px solid #b3d9f7; padding: 2px 8px; border-radius: 20px; font-size: 0.68rem; font-weight: 700; }
        .mnu-section-actions { display: flex; gap: 5px; }
        .mnu-sec-btn { height: 26px; padding: 0 8px; border-radius: 6px; border: none; font-size: 0.7rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 3px; }
        .mnu-sec-btn-edit { background: #e3f2fd; color: #1565c0; }
        .mnu-sec-btn-edit:hover { background: #bbdefb; }
        .mnu-sec-btn-del { background: #fce4ec; color: #c62828; }
        .mnu-sec-btn-del:hover { background: #f8bbd9; }
        .mnu-sec-btn-add { background: #0e7490; color: #fff; }
        .mnu-sec-btn-add:hover { background: #0c6480; }

        /* Edit section inline */
        .mnu-edit-section-form { padding: 10px 16px; background: #fff8e1; border-bottom: 1px solid #ffe082; }
        .mnu-edit-section-form input { width: 100%; padding: 7px 10px; border: 1.5px solid #e4e8ef; border-radius: 7px; font-size: 0.83rem; outline: none; margin-bottom: 6px; }

        /* Products grid */
        .mnu-products { padding: 14px 16px; }
        .mnu-products-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; }
        .mnu-product-card { border: 1px solid #e6eaf0; border-radius: 14px; overflow: hidden; transition: box-shadow 0.18s ease, transform 0.18s ease; background: #fff; box-shadow: 0 1px 2px rgba(16,24,40,0.04); }
        .mnu-product-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.1); transform: translateY(-2px); }
        .mnu-product-card { position: relative; }
        .mnu-product-card.is-selected { border-color: #0e7490; box-shadow: 0 0 0 2px rgba(14,116,144,0.14); }
        .mnu-product-checkbox { position: absolute; top: 7px; right: 7px; z-index: 3; width: 18px; height: 18px; accent-color: #0e7490; cursor: pointer; }
        .mnu-no-photo-tag { position: absolute; top: 10px; left: 10px; z-index: 2; background: rgba(200,136,10,0.92); color: #fff; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.1px; padding: 4px 9px; border-radius: 999px; display: inline-flex; align-items: center; gap: 5px; backdrop-filter: blur(4px); }
        .mnu-photoless-btn { display: inline-flex; align-items: center; gap: 5px; border: 1.5px solid #e0b800; background: #fff8e6; color: #8a6a1e; font-size: 0.72rem; font-weight: 800; padding: 6px 11px; border-radius: 6px; white-space: nowrap; }
        .mnu-photoless-btn:hover { background: #fff1cc; }
        .mnu-photoless-btn.is-active { background: #c8880a; border-color: #c8880a; color: #fff; }
        .mnu-select-visible { display: inline-flex; align-items: center; gap: 6px; height: 33px; padding: 0 10px; border: 1.5px solid #cbd5e1; border-radius: 8px; background: #fff; color: #475569; font-size: 0.72rem; font-weight: 700; cursor: pointer; white-space: nowrap; }
        .mnu-select-visible input, .mnu-item-checkbox { accent-color: #0e7490; cursor: pointer; }
        .mnu-live-only { display: inline-flex; align-items: center; gap: 6px; height: 33px; padding: 0 10px; border: 1.5px solid #cbd5e1; border-radius: 8px; background: #fff; color: #475569; font-size: 0.72rem; font-weight: 800; cursor: pointer; white-space: nowrap; user-select: none; }
        .mnu-live-only.is-on { border-color: #2e7d32; background: #e8f5e9; color: #2e7d32; }
        .mnu-live-only input { position: absolute; opacity: 0; width: 1px; height: 1px; }
        .mnu-live-only:focus-within { outline: 2px solid #0e7490; outline-offset: 2px; }
        .mnu-live-only-count { font-weight: 700; opacity: 0.8; padding-left: 6px; border-left: 1px solid currentColor; }
        .mnu-link-btn { background: none; border: none; padding: 0; color: #0e7490; font: inherit; font-weight: 700; text-decoration: underline; cursor: pointer; }
        .mnu-bulk-delete-btn { height: 33px; padding: 0 12px; border: none; border-radius: 8px; background: #dc3545; color: #fff; font-size: 0.75rem; font-weight: 800; display: inline-flex; align-items: center; gap: 5px; cursor: pointer; white-space: nowrap; }
        .mnu-clear-selection { border: none; background: none; color: #64748b; font-size: 0.72rem; font-weight: 700; cursor: pointer; padding: 4px; }
        .mnu-item-checkbox { width: 16px; height: 16px; flex-shrink: 0; }
        .mnu-publish-bar { display: flex; align-items: center; gap: 12px; padding: 12px 18px; border-bottom: 1px solid #e6ebf1; }
        .mnu-publish-bar.is-draft { background: #fff8e6; border-left: 4px solid #c8880a; }
        .mnu-publish-bar.is-live { background: #eefaf1; border-left: 4px solid #16834a; }
        .mnu-publish-icon { flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; }
        .mnu-publish-bar.is-draft .mnu-publish-icon { background: #c8880a; color: #fff; }
        .mnu-publish-bar.is-live .mnu-publish-icon { background: #16834a; color: #fff; }
        .mnu-publish-copy { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .mnu-publish-copy strong { font-size: 0.82rem; font-weight: 800; color: #23282d; }
        .mnu-publish-copy small { font-size: 0.72rem; color: #6b7c93; line-height: 1.4; }
        .mnu-publish-btn { flex-shrink: 0; border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.75rem; font-weight: 800; color: #fff; }
        .mnu-publish-bar.is-draft .mnu-publish-btn { background: #16834a; }
        .mnu-publish-bar.is-live .mnu-publish-btn { background: #8a6a1e; }
        .mnu-publish-btn:disabled { opacity: 0.6; }
        /* Dish photos are uploaded at 1000x1000, so the frame is SQUARE and
           follows the card's width: the whole photo shows, edge to edge, with
           no crop and no side bars. A fixed 110px strip and then a 4:3 frame
           both cut or letterboxed it. The placeholder is contained so its
           plate never scales up blurry. */
        .mnu-product-img { width: 100%; aspect-ratio: 1 / 1; height: auto; object-fit: cover; background: #f5f6f8; display: block; position: relative; z-index: 1; box-shadow: 0 10px 18px -12px rgba(16,24,40,0.35), 0 1px 0 rgba(16,24,40,0.06); }
        /* The placeholder asset is 900x900, the same shape as an upload, so it
           fills the frame exactly like a photo would; only its tone is softer. */
        .mnu-product-img.is-placeholder { opacity: 0.85; }
        .mnu-product-body { padding: 14px 14px 14px; }
        .mnu-product-name { font-weight: 600; font-size: 0.9rem; line-height: 1.3; letter-spacing: -0.01em; color: #111827; margin-bottom: 2px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .mnu-product-price { font-size: 0.92rem; color: #047857; font-weight: 700; letter-spacing: -0.01em; margin-bottom: 10px; }
        .mnu-product-status { display: inline-flex; align-items: center; gap: 7px; font-size: 0.76rem; font-weight: 600; padding: 7px 11px; border-radius: 9px; margin-bottom: 8px; border: none; font-family: inherit; width: 100%; justify-content: flex-start; text-align: left; transition: filter 0.15s ease, box-shadow 0.15s ease; }
        .mnu-product-status.is-toggle { cursor: pointer; }
        .mnu-product-status.is-toggle:hover:not(:disabled) { filter: brightness(0.95); box-shadow: 0 0 0 2px rgba(14,116,144,0.18); }
        .mnu-product-status:disabled { cursor: default; opacity: 0.85; }
        .mnu-bulk-status-btn { height: 33px; padding: 0 12px; border: 1.5px solid transparent; border-radius: 8px; font-size: 0.75rem; font-weight: 800; display: inline-flex; align-items: center; gap: 5px; cursor: pointer; white-space: nowrap; }
        .mnu-bulk-status-btn.is-on { background: #e8f5e9; border-color: #a5d6a7; color: #2e7d32; }
        .mnu-bulk-status-btn.is-off { background: #fce4ec; border-color: #f2b8c6; color: #c62828; }
        .mnu-bulk-status-btn:disabled { opacity: 0.55; cursor: default; }
        .mnu-product-actions { display: flex; gap: 8px; }
        .mnu-prod-btn { flex: 1; height: 34px; border-radius: 9px; border: none; font-size: 0.78rem; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; transition: background 0.15s ease; }
        .mnu-prod-btn-edit { background: #e3f2fd; color: #1565c0; }
        .mnu-prod-btn-edit:hover { background: #bbdefb; }
        .mnu-prod-btn-del { background: #fce4ec; color: #c62828; }
        .mnu-prod-btn-del:hover { background: #f8bbd9; }
        .mnu-no-products { text-align: center; padding: 20px; color: #b0bec5; font-size: 0.8rem; font-style: italic; }

        @media (max-width: 600px) {
          .mnu-drawer { width: 100vw; }
          .mnu-products-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
          .mnu-stats { display: none; }
        }
      `}</style>

      {loading && (
        <Spinner animation="grow" variant="success"
          style={{ position: "fixed", top: "50%", left: "50%", zIndex: 9999 }}
        />
      )}

      <div className="rst-page">

        {/* ── KPI Cards ── */}
        <div className="rst-kpi-grid">
          <div className="rst-kpi-card" style={{ borderTopColor: "#4169E1" }}>
            <div className="rst-kpi-icon"><FaUtensils /></div>
            <div className="rst-kpi-label">Total Restaurants</div>
            <div className="rst-kpi-value" style={{ color: "#4169E1" }}>{totalRestaurants}</div>
            <div className="rst-kpi-sub">Registered outlets</div>
          </div>
          <div className="rst-kpi-card" style={{ borderTopColor: "#117a44" }}>
            <div className="rst-kpi-icon"><FaToggleOn /></div>
            <div className="rst-kpi-label">Active</div>
            <div className="rst-kpi-value" style={{ color: "#117a44" }}>{activeRestaurants}</div>
            <div className="rst-kpi-sub">Currently serving customers</div>
          </div>
          <div className="rst-kpi-card" style={{ borderTopColor: "#c8880a" }}>
            <div className="rst-kpi-icon"><FaStore /></div>
            <div className="rst-kpi-label">Total Outlets</div>
            <div className="rst-kpi-value" style={{ color: "#c8880a" }}>{totalOutlets}</div>
            <div className="rst-kpi-sub">Branch stores</div>
          </div>
          <div className="rst-kpi-card" style={{ borderTopColor: "#17a2b8" }}>
            <div className="rst-kpi-icon"><FaClock /></div>
            <div className="rst-kpi-label">Hours Configured</div>
            <div className="rst-kpi-value" style={{ color: "#17a2b8" }}>{hoursConfigured}</div>
            <div className="rst-kpi-sub">With operating hours set</div>
          </div>
        </div>

        {/* ── Main Panel ── */}
        <div className="rst-panel">

          {/* Dark header */}
          <div className="rst-header">
            <div>
              <h4 className="rst-header-title">
                <FaUtensils />
                Restaurants Directory
              </h4>
              <p className="rst-header-sub">Manage all registered restaurants and their outlets</p>
            </div>
          </div>

          {/* Filter bar */}
          {!editingRestaurant && (
            <div className="rst-filter-bar">
              <div className="rst-filter-search">
                <span className="rst-filter-search-icon"><FaSearch /></span>
                <input
                  type="text"
                  placeholder="Search restaurants…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <span className="rst-filter-label">Status</span>
              <AppSelect className="rst-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </AppSelect>

              <span className="rst-filter-label">Hours</span>
              <AppSelect className="rst-filter-select" value={hoursFilter} onChange={(e) => setHoursFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="configured">Configured</option>
                <option value="not-set">Not Set</option>
              </AppSelect>

              <span className="rst-filter-label">Outlets</span>
              <AppSelect className="rst-filter-select" value={outletsFilter} onChange={(e) => setOutletsFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="with">Has Outlets</option>
                <option value="without">No Outlets</option>
              </AppSelect>

              {hasActiveFilters && (
                <button className="rst-filter-clear" onClick={clearFilters}>
                  <FaFilter style={{ fontSize: 10 }} /> Clear
                </button>
              )}

              <span className="rst-filter-results">
                {filteredRestaurants.length} of {totalRestaurants}
              </span>
            </div>
          )}

          {/* Edit form inline */}
          {editingRestaurant && (
            <div style={{ padding: "20px" }}>
              <EditRestaurantForm
                restaurant={editingRestaurant}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            </div>
          )}

          {/* Error state */}
          {!editingRestaurant && error && (
            <div className="rst-error">
              <FaExclamationCircle style={{ fontSize: 20 }} />
              {error}
              <button
                onClick={handleRetry}
                style={{ marginLeft: "auto", background: "#c62828", color: "#fff", border: "none", borderRadius: 7, padding: "6px 16px", fontWeight: 700, fontSize: "0.8rem", cursor: "pointer" }}
              >
                <FaRedo style={{ marginRight: 5 }} />Retry
              </button>
            </div>
          )}

          {/* Desktop Table */}
          {!editingRestaurant && !error && (
            <div className="rst-table-wrap">
              {/* Five columns sized to fit a laptop screen, so the status chips and
                  every action are visible without scrolling sideways. Logo, name,
                  address and ID share one cell; the AI toggle sits with the other
                  switch; hours, commission and menu read as one "setup" column;
                  outlets collapse to a count that expands under the row. */}
              <table className="rst-table rst-table-fit">
                <colgroup>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "27%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Restaurant</th>
                    <th>Outlets</th>
                    <th>Status</th>
                    <th>Setup</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!filteredRestaurants.length ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="rst-empty">
                          <div className="rst-empty-icon"><FaUtensils /></div>
                          <div className="rst-empty-msg">
                            {hasActiveFilters ? "No restaurants match the current filters." : "No restaurants found."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredRestaurants.map((restaurant, index) => {
                      const outletCount = restaurant.outlets?.length || 0;
                      const outletsOpen = !!expandedOutletRows[restaurant._id];
                      const sectionCount = (restaurant.sections || []).filter((sec) => sec && sec._id).length;
                      const productCount = (restaurant.sections || []).reduce((n, sec) => n + (sec?.products?.length || 0), 0);
                      return (
                      <React.Fragment key={restaurant._id}>
                      <tr>
                        <td>
                          <div className="rst-ident">
                            <span className="rst-ident-index">{index + 1}</span>
                            <LogoAvatar name={restaurant.name} imageUrl={restaurant.imageUrl} size={64} radius={12} fontSize={20} />
                            <div className="rst-ident-text">
                              <div className="rst-name" title={restaurant.name}>{restaurant.name}</div>
                              <div className="rst-addr" title={restaurant.address || ""}>
                                <FaMapMarkerAlt style={{ fontSize: 10, color: "#5bc5f5" }} />
                                <span className="rst-addr-text">{restaurant.address || "No address"}</span>
                              </div>
                              <button
                                type="button"
                                className="rst-id-pill rst-id-pill-sm"
                                title={`${restaurant._id}, click to copy`}
                                onClick={() => {
                                  try {
                                    navigator.clipboard.writeText(restaurant._id);
                                    toast.success("Restaurant ID copied.");
                                  } catch (e) {
                                    toast.error("Couldn't copy the ID.");
                                  }
                                }}
                              >
                                <FaCopy />{restaurant._id?.slice(-8)}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td>
                          {outletCount > 0 ? (
                            <button
                              type="button"
                              className="rst-outlet-count rst-outlet-toggle"
                              aria-expanded={outletsOpen}
                              onClick={() => setExpandedOutletRows((prev) => ({ ...prev, [restaurant._id]: !prev[restaurant._id] }))}
                            >
                              <FaStore style={{ fontSize: 9 }} />
                              {outletCount} {outletCount === 1 ? "Store" : "Stores"}
                              {outletsOpen ? <FaChevronDown style={{ fontSize: 8 }} /> : <FaChevronRight style={{ fontSize: 8 }} />}
                            </button>
                          ) : (
                            <span style={{ color: "#b0bec5", fontStyle: "italic", fontSize: "0.76rem", whiteSpace: "nowrap" }}>
                              <FaStoreSlash style={{ marginRight: 5 }} />No stores
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="rst-stack">
                            <div className="rst-switch-line">
                              <Switch
                                checked={restaurant.isActive}
                                disabled={!canApproveRestaurants || toggleLoading[restaurant._id]}
                                onChange={() => handleToggleActive(restaurant._id, restaurant.isActive, restaurant.name)}
                                size="small"
                                classes={{ switchBase: classes.switchBase, checked: classes.checked, track: classes.track }}
                              />
                              <span className="rst-status" style={restaurant.isActive
                                ? { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" }
                                : { background: "#fce4ec", color: "#c62828", border: "1px solid #f5c6cb" }}>
                                {restaurant.isActive ? "Active" : "Inactive"}
                              </span>
                            </div>
                            <div className="rst-switch-line">
                              <Switch
                                checked={!!restaurant.isAIShoppable}
                                disabled={!canManageRestaurants || aiToggleLoading[restaurant._id]}
                                onChange={() => handleToggleAIShoppable(restaurant._id, restaurant.name, restaurant.isAIShoppable)}
                                size="small"
                                classes={{ switchBase: classes.switchBase, checked: classes.checked, track: classes.track }}
                              />
                              <span className="rst-status" style={restaurant.isAIShoppable
                                ? { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" }
                                : { background: "#f4f6f9", color: "#9aacbf", border: "1px solid #e4e8ef" }}>
                                <FaRobot style={{ fontSize: 9 }} />
                                {restaurant.isAIShoppable ? "AI On" : "AI Off"}
                              </span>
                            </div>
                            {/* A draft has never been live. Shown beside Active
                                rather than instead of it, because they answer
                                different questions: "suspended?" and "released
                                to customers yet?". */}
                            {restaurant.publishStatus === "DRAFT" && (
                              <span
                                className="rst-status"
                                style={{ background: "#fff8e6", color: "#8a6a1e", border: "1px solid #e0b800" }}
                                title="Not visible to customers. Open Menu Management to publish it."
                              >
                                <FaEyeSlash style={{ fontSize: 9 }} />
                                Draft
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="rst-stack">
                            {restaurant.openHours ? (
                              <span className="rst-hours-badge"
                                style={{ background: "#e0f7fa", color: "#00695c", border: "1px solid #b2dfdb" }}
                                onClick={() => canUpdateRestaurants && handleManageHours(restaurant)}
                                title="Click to manage hours"
                              >
                                <FaClock style={{ fontSize: 9 }} />Hours set
                              </span>
                            ) : (
                              <span className="rst-hours-badge"
                                style={{ background: "#fff8e1", color: "#f57f17", border: "1px solid #ffe082" }}
                                onClick={() => canUpdateRestaurants && handleManageHours(restaurant)}
                                title="Click to set hours"
                              >
                                <FaClock style={{ fontSize: 9 }} />Hours not set
                              </span>
                            )}
                            <span className="rst-hours-badge" style={{ background: "#fff3e0", color: "#b45309", border: "1px solid #fed7aa", cursor: "default" }}>
                              {Number(restaurant.commissionRate ?? 8).toFixed(2).replace(/\.00$/, "")}% commission{restaurant.commissionConfigured ? "" : " · Review"}
                            </span>
                            <span className="mnu-stat-pill" title={`${sectionCount} section${sectionCount !== 1 ? "s" : ""}, ${productCount} product${productCount !== 1 ? "s" : ""}`}>
                              <FaListUl style={{ fontSize: 8 }} />{sectionCount} sec · <FaTag style={{ fontSize: 8 }} />{productCount} items
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="rst-actions rst-actions-grid">
                            <button className="rst-btn rst-btn-menu" onClick={() => handleOpenMenuPanel(restaurant)}>
                              <FaListUl style={{ fontSize: 10 }} /> View Menu
                            </button>
                            <button className="rst-btn rst-btn-edit" onClick={() => handleEditClick(restaurant)} disabled={!canUpdateRestaurants}>
                              <FaEdit style={{ fontSize: 10 }} /> Edit
                            </button>
                            <button className="rst-btn rst-btn-hours" onClick={() => handleManageHours(restaurant)} disabled={!canUpdateRestaurants}>
                              <FaClock style={{ fontSize: 10 }} /> Hours
                            </button>
                            <button className="rst-btn rst-btn-attach" onClick={() => handleAttachStoreClick(restaurant)} disabled={!canCreateRestaurants}>
                              <FaCodeBranch style={{ fontSize: 10 }} /> Attach Store
                            </button>
                            {/* Super-admin only: mail the vendor their login
                                email + a single-use set-password link. */}
                            <SendVendorCredentialsAction
                              vendorType="RESTAURANT"
                              vendorId={restaurant._id}
                              vendorName={restaurant.name}
                              visible={isSuperAdmin}
                            >
                              {({ open, busy }) => (
                                <button className="rst-btn rst-btn-creds" onClick={open} disabled={busy}>
                                  <FaKey style={{ fontSize: 10 }} /> {busy ? "Sending..." : "Send Login"}
                                </button>
                              )}
                            </SendVendorCredentialsAction>
                            <SignInAsVendorButton
                              ownerId={restaurant.owner}
                              vendorId={restaurant._id}
                              vendorName={restaurant.name}
                              className="rst-btn rst-btn-asvendor"
                            />
                            <button className="rst-btn rst-btn-del" onClick={() => handleDeleteRestaurant(restaurant.owner)} disabled={!canDeleteRestaurants}>
                              <FaTrash style={{ fontSize: 10 }} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                      {outletsOpen && outletCount > 0 && (
                        <tr className="rst-outlet-row">
                          <td colSpan={5}>
                            <div className="rst-outlet-grid">
                              {restaurant.outlets.map((outlet) => (
                                <div key={outlet._id} className="rst-outlet-item">
                                  <div className="rst-outlet-name">
                                    {outlet.name}
                                    <span className="rst-status" style={outlet.isActive !== false
                                      ? { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" }
                                      : { background: "#fce4ec", color: "#c62828", border: "1px solid #f5c6cb" }}>
                                      <FaCircle style={{ fontSize: 6 }} />
                                      {outlet.isActive !== false ? "Active" : "Inactive"}
                                    </span>
                                  </div>
                                  <div className="rst-outlet-meta">
                                    <FaMapMarkerAlt style={{ fontSize: 9 }} />{outlet.address}
                                  </div>
                                  <div className="rst-outlet-meta">
                                    <FaRoute style={{ fontSize: 9 }} />Radius: {outlet.deliveryZoneRadius} km
                                  </div>
                                  <div className="rst-outlet-actions">
                                    {/* The outlet's menu: how many of the parent's dishes it
                                        serves, and the picker to change that. Amber when it
                                        serves none, because an attached store with an empty
                                        menu shows customers nothing. */}
                                    {(() => {
                                      const served = (outlet.menuItems || []).length;
                                      return (
                                        <button
                                          className="lk-action-btn"
                                          onClick={() => setOutletMenuTarget({ outlet, restaurant })}
                                          disabled={!canUpdateRestaurants}
                                          title={served === 0 ? "This outlet serves no dishes yet. Choose its menu." : "Choose which dishes this outlet serves"}
                                        >
                                          <FaUtensils style={{ fontSize: 10 }} />
                                          Menu Items · {served} of {productCount}
                                          {served === 0 ? <span className="rst-outlet-menu-empty" title="Serves nothing yet" /> : null}
                                        </button>
                                      );
                                    })()}
                                    <Switch
                                      checked={outlet.isActive !== false}
                                      disabled={!canApproveRestaurants || toggleOutletLoading[outlet._id]}
                                      onChange={() => handleToggleOutletActive(outlet._id, outlet.isActive !== false, outlet.name)}
                                      size="small"
                                      classes={{ switchBase: classes.switchBase, checked: classes.checked, track: classes.track }}
                                    />
                                    <button
                                      className="lk-action-btn lk-action-btn--outline lk-action-btn--icon"
                                      onClick={() => handleEditOutlet(outlet)}
                                      disabled={!canUpdateRestaurants}
                                      title="Edit outlet"
                                    >
                                      <FaEdit style={{ fontSize: 10 }} />
                                    </button>
                                    <button
                                      className="rst-outlet-btn rst-outlet-btn-del"
                                      onClick={() => handleDeleteOutlet(outlet)}
                                      disabled={!canDeleteRestaurants}
                                      title="Delete outlet"
                                    >
                                      <FaTrash style={{ fontSize: 10 }} />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Mobile Card List */}
          {!editingRestaurant && !error && (
            <div className="rst-card-list">
              {!filteredRestaurants.length ? (
                <div className="rst-empty">
                  <div className="rst-empty-icon"><FaUtensils /></div>
                  <div className="rst-empty-msg">
                    {hasActiveFilters ? "No restaurants match the current filters." : "No restaurants found."}
                  </div>
                </div>
              ) : (
                filteredRestaurants.map((restaurant) => (
                  <div key={restaurant._id} className="rst-mobile-card">
                    <div className="rst-mc-header">
                      <LogoAvatar name={restaurant.name} imageUrl={restaurant.imageUrl} size={56} radius={11} fontSize={18} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="rst-mc-name">{restaurant.name}</div>
                        <div className="rst-mc-addr">{restaurant.address || "—"}</div>
                      </div>
                    </div>
                    <div className="rst-mc-badges">
                      <span className="rst-status" style={restaurant.isActive
                        ? { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" }
                        : { background: "#fce4ec", color: "#c62828", border: "1px solid #f5c6cb" }}>
                        {restaurant.isActive ? <FaToggleOn style={{ fontSize: 9 }} /> : <FaToggleOn style={{ fontSize: 9, transform: "scaleX(-1)" }} />}
                        {restaurant.isActive ? "Active" : "Inactive"}
                      </span>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.65rem',
                        fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                        ...(restaurant.isAIShoppable
                          ? { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }
                          : { background: '#f4f6f9', color: '#9aacbf', border: '1px solid #e4e8ef' })
                      }}>
                        {restaurant.isAIShoppable ? '🤖 AI On' : 'AI Off'}
                      </span>
                      <span className="rst-hours-badge" style={{ background: "#fff3e0", color: "#b45309", border: "1px solid #fed7aa" }}>
                        Commission {Number(restaurant.commissionRate ?? 8).toFixed(2).replace(/\.00$/, "")}%{restaurant.commissionConfigured ? "" : " · Review"}
                      </span>
                      {restaurant.outlets?.length > 0 && (
                        <span className="rst-outlet-count">
                          <FaStore style={{ fontSize: 9 }} />
                          {restaurant.outlets.length} {restaurant.outlets.length === 1 ? "Store" : "Stores"}
                        </span>
                      )}
                      {restaurant.openHours
                        ? <span className="rst-hours-badge" style={{ background: "#e0f7fa", color: "#00695c", border: "1px solid #b2dfdb" }}><FaClock style={{ fontSize: 9 }} />Hours Set</span>
                        : <span className="rst-hours-badge" style={{ background: "#fff8e1", color: "#f57f17", border: "1px solid #ffe082" }}><FaClock style={{ fontSize: 9 }} />No Hours</span>
                      }
                      {(() => {
                        const sectionCount = (restaurant.sections || []).filter(s => s && s._id).length;
                        const productCount = (restaurant.sections || []).reduce((n, s) => n + (s?.products?.length || 0), 0);
                        return (
                          <>
                            <span className="mnu-stat-pill" style={{ fontSize: "0.72rem", padding: "2px 7px" }}>
                              <FaListUl style={{ fontSize: 8, marginRight: 3 }} />{sectionCount} section{sectionCount !== 1 ? "s" : ""}
                            </span>
                            <span className="mnu-stat-pill" style={{ fontSize: "0.72rem", padding: "2px 7px", background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" }}>
                              <FaTag style={{ fontSize: 8, marginRight: 3 }} />{productCount} product{productCount !== 1 ? "s" : ""}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    <div className="rst-mc-actions">
                      <button className="rst-btn rst-btn-menu" onClick={() => handleOpenMenuPanel(restaurant)}><FaListUl style={{ fontSize: 10 }} /> View Menu</button>
                      <button className="rst-btn rst-btn-edit" onClick={() => handleEditClick(restaurant)} disabled={!canUpdateRestaurants}><FaEdit style={{ fontSize: 10 }} /> Edit</button>
                      <button className="rst-btn rst-btn-del" onClick={() => handleDeleteRestaurant(restaurant.owner)} disabled={!canDeleteRestaurants}><FaTrash style={{ fontSize: 10 }} /> Delete</button>
                      <button className="rst-btn rst-btn-attach" onClick={() => handleAttachStoreClick(restaurant)} disabled={!canCreateRestaurants}><FaCodeBranch style={{ fontSize: 10 }} /> Attach Store</button>
                      <button className="rst-btn rst-btn-hours" onClick={() => handleManageHours(restaurant)} disabled={!canUpdateRestaurants}><FaClock style={{ fontSize: 10 }} /> Hours</button>
                      <SendVendorCredentialsAction
                        vendorType="RESTAURANT"
                        vendorId={restaurant._id}
                        vendorName={restaurant.name}
                        visible={isSuperAdmin}
                      >
                        {({ open, busy }) => (
                          <button className="rst-btn rst-btn-creds" onClick={open} disabled={busy}><FaKey style={{ fontSize: 10 }} /> {busy ? "Sending…" : "Send Login"}</button>
                        )}
                      </SendVendorCredentialsAction>
                      <SignInAsVendorButton
                        ownerId={restaurant.owner}
                        vendorId={restaurant._id}
                        vendorName={restaurant.name}
                        className="rst-btn rst-btn-asvendor"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── Outlet Menu Items (shared picker) ── */}
      <OutletMenuItemsModal
        show={!!outletMenuTarget}
        outlet={outletMenuTarget?.outlet}
        sections={outletMenuTarget?.restaurant?.sections}
        onHide={() => setOutletMenuTarget(null)}
        onSave={handleSaveOutletMenuItems}
      />

      {/* ── Attach Store Modal ── */}
      <Modal show={showAttachStoreModal} onHide={toggleAttachStoreModal} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #4B0082", background: "#fafbfc" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaCodeBranch style={{ color: "#4B0082" }} />
            Attach Store — {selectedRestaurant?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          <Form>
            <Form.Group controlId="storeName" style={{ marginBottom: 16 }}>
              <label className="rst-modal-label">Store Name</label>
              <Form.Control
                className="rst-modal-input"
                type="text"
                placeholder="Enter store name"
                name="name"
                value={storeDetails.name}
                onChange={handleStoreDetailsChange}
              />
            </Form.Group>
            <Form.Group controlId="storeLocation" style={{ marginBottom: 16 }}>
              <label className="rst-modal-label">Store Location</label>
              <LocationAutocomplete
                value={value}
                setValue={setValue}
                ready={ready}
                data={data}
                clearSuggestions={clearSuggestions}
                mapsApiLoaded={mapsApiLoaded}
                loadingSuggestions={placesLoading} suggestionStatus={suggestionStatus}
                defaultValue={editingOutlet?.address}
                onLocationSelect={({ address, latitude, longitude }) => {
                  setStoreDetails((prev) => ({ ...prev, location: address, latitude, longitude }));
                }}
              />
            </Form.Group>
            <Divider style={{ margin: "10px 0" }} />
            <Form.Group controlId="storeRadius" style={{ marginTop: 15 }}>
              <label className="rst-modal-label">Delivery Zone Radius (km)</label>
              <Form.Control
                className="rst-modal-input"
                type="number"
                placeholder="Enter delivery radius in kilometres"
                name="deliveryZoneRadius"
                value={storeDetails.deliveryZoneRadius}
                onChange={handleStoreDetailsChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f0f2f5" }}>
          <button className="rst-modal-footer-btn" onClick={toggleAttachStoreModal} disabled={isSubmitting}
            style={{ background: "#f4f6f9", color: "#6b7c93", border: "1.5px solid #e4e8ef" }}>
            Cancel
          </button>
          <button className="rst-modal-footer-btn" onClick={handleAttachStore} disabled={isSubmitting}
            style={{ background: "#4B0082", color: "#fff", opacity: isSubmitting ? 0.7 : 1 }}>
            {isSubmitting ? (
              <><Spinner as="span" animation="border" size="sm" style={{ color: "#fff" }} />&nbsp;Attaching…</>
            ) : (
              <><FaCodeBranch style={{ fontSize: 11 }} />Attach</>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Restaurant Modal ── */}
      <Modal show={showDeleteModal} onHide={toggleDeleteModal} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #dc3545", background: "#fafbfc" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaTrash style={{ color: "#dc3545" }} />
            Confirm Restaurant Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", fontSize: "0.9rem" }}>
          This action is <strong>irreversible</strong>. The restaurant and all associated data will be permanently removed.
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f0f2f5" }}>
          <button className="rst-modal-footer-btn" onClick={toggleDeleteModal}
            style={{ background: "#f4f6f9", color: "#6b7c93", border: "1.5px solid #e4e8ef" }}>
            Cancel
          </button>
          <button className="rst-modal-footer-btn" onClick={confirmDeleteRestaurant}
            style={{ background: "#dc3545", color: "#fff" }}>
            <FaTrash style={{ fontSize: 11 }} /> Delete
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Edit Outlet Modal ── */}
      <Modal show={showEditOutletModal} onHide={() => setShowEditOutletModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #4169E1", background: "#fafbfc" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaEdit style={{ color: "#4169E1" }} />
            Edit Outlet — {editingOutlet?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          <Form>
            <Form.Group style={{ marginBottom: 16 }}>
              <label className="rst-modal-label">Store Name</label>
              <Form.Control
                className="rst-modal-input"
                type="text"
                placeholder="Enter store name"
                defaultValue={editingOutlet?.name}
                onChange={(e) => setEditingOutlet({ ...editingOutlet, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group style={{ marginBottom: 16 }}>
              <label className="rst-modal-label">Address</label>
              <LocationAutocomplete
                value={value}
                setValue={setValue}
                ready={ready}
                data={data}
                clearSuggestions={clearSuggestions}
                mapsApiLoaded={mapsApiLoaded}
                loadingSuggestions={placesLoading} suggestionStatus={suggestionStatus}
                defaultValue={editingOutlet?.address}
                onLocationSelect={({ address, latitude, longitude }) => {
                  setEditingOutlet({ ...editingOutlet, address, latitude, longitude });
                }}
              />
            </Form.Group>
            <Form.Group>
              <label className="rst-modal-label">Delivery Zone Radius (km)</label>
              <Form.Control
                className="rst-modal-input"
                type="number"
                placeholder="Enter radius in kilometres"
                defaultValue={editingOutlet?.deliveryZoneRadius}
                onChange={(e) => setEditingOutlet({ ...editingOutlet, deliveryZoneRadius: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f0f2f5" }}>
          <button className="rst-modal-footer-btn" onClick={() => setShowEditOutletModal(false)}
            style={{ background: "#f4f6f9", color: "#6b7c93", border: "1.5px solid #e4e8ef" }}>
            Cancel
          </button>
          <button className="rst-modal-footer-btn" onClick={() => handleUpdateOutlet(editingOutlet)}
            style={{ background: "#4169E1", color: "#fff" }}>
            <FaEdit style={{ fontSize: 11 }} /> Save Changes
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Delete Outlet Modal ── */}
      <Modal show={showDeleteOutletModal} onHide={() => setShowDeleteOutletModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #dc3545", background: "#fafbfc" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaTrash style={{ color: "#dc3545" }} />
            Delete Outlet
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          <div style={{ textAlign: "center" }}>
            <FaExclamationTriangle style={{ fontSize: "2.5rem", color: "#dc3545", marginBottom: 12 }} />
            <p style={{ fontSize: "0.9rem", marginBottom: 12 }}>Are you sure you want to delete this outlet?</p>
            <div style={{ background: "#fce4ec", border: "1.5px solid #f5c6cb", borderRadius: 8, padding: "10px 16px", marginBottom: 10 }}>
              <strong style={{ color: "#c62828" }}>{outletToDelete?.name}</strong>
              <div style={{ fontSize: "0.78rem", color: "#6b7c93", marginTop: 4 }}>
                <FaMapMarkerAlt style={{ marginRight: 4 }} />{outletToDelete?.address}
              </div>
            </div>
            <p style={{ fontSize: "0.78rem", color: "#9aacbf" }}>This action cannot be undone.</p>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f0f2f5" }}>
          <button className="rst-modal-footer-btn" onClick={() => setShowDeleteOutletModal(false)}
            style={{ background: "#f4f6f9", color: "#6b7c93", border: "1.5px solid #e4e8ef" }}>
            Cancel
          </button>
          <button className="rst-modal-footer-btn" onClick={confirmDeleteOutlet}
            style={{ background: "#dc3545", color: "#fff" }}>
            <FaTrash style={{ fontSize: 11 }} /> Delete Outlet
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Activate / Deactivate Confirmation Modal ── */}
      <Modal show={!!toggleConfirm} onHide={() => setToggleConfirm(null)} centered>
        <Modal.Header closeButton style={{ borderBottom: `2px solid ${toggleConfirm?.currentStatus ? "#dc3545" : "#2e7d32"}`, background: "#fafbfc" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaToggleOn style={toggleConfirm?.currentStatus ? { color: "#dc3545", transform: "scaleX(-1)" } : { color: "#2e7d32" }} />
            {toggleConfirm?.currentStatus ? "Deactivate" : "Activate"} {toggleConfirm?.type === "outlet" ? "Outlet" : "Restaurant"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          <div style={{ textAlign: "center" }}>
            {toggleConfirm?.currentStatus ? (
              <FaExclamationTriangle style={{ fontSize: "2.5rem", color: "#dc3545", marginBottom: 12 }} />
            ) : (
              <FaToggleOn style={{ fontSize: "2.5rem", color: "#2e7d32", marginBottom: 12 }} />
            )}
            <p style={{ fontSize: "0.9rem", marginBottom: 12 }}>
              Are you sure you want to {toggleConfirm?.currentStatus ? "deactivate" : "activate"} this {toggleConfirm?.type === "outlet" ? "outlet" : "restaurant"}?
            </p>
            <div style={toggleConfirm?.currentStatus
              ? { background: "#fce4ec", border: "1.5px solid #f5c6cb", borderRadius: 8, padding: "10px 16px", marginBottom: 10 }
              : { background: "#e8f5e9", border: "1.5px solid #c8e6c9", borderRadius: 8, padding: "10px 16px", marginBottom: 10 }}>
              <strong style={{ color: toggleConfirm?.currentStatus ? "#c62828" : "#2e7d32" }}>{toggleConfirm?.name}</strong>
            </div>
            <p style={{ fontSize: "0.78rem", color: "#9aacbf", marginBottom: 0 }}>
              {toggleConfirm?.currentStatus
                ? `Customers will no longer see this ${toggleConfirm?.type === "outlet" ? "outlet" : "restaurant"} and new orders will be blocked until it is reactivated.`
                : `This ${toggleConfirm?.type === "outlet" ? "outlet" : "restaurant"} will become visible to customers and can start receiving orders right away.`}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f0f2f5" }}>
          <button className="rst-modal-footer-btn" onClick={() => setToggleConfirm(null)}
            style={{ background: "#f4f6f9", color: "#6b7c93", border: "1.5px solid #e4e8ef" }}>
            Cancel
          </button>
          <button className="rst-modal-footer-btn" onClick={performToggle}
            style={{ background: toggleConfirm?.currentStatus ? "#dc3545" : "#2e7d32", color: "#fff" }}>
            <FaToggleOn style={toggleConfirm?.currentStatus ? { fontSize: 11, transform: "scaleX(-1)" } : { fontSize: 11 }} />{" "}
            {toggleConfirm?.currentStatus ? "Deactivate" : "Activate"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Bulk menu import (workbook + photos) ── */}
      <MenuImportModal
        show={showMenuImport}
        restaurant={menuRestaurant}
        existingSections={menuSections}
        onClose={() => setShowMenuImport(false)}
        onImported={() => {
          if (menuRestaurant) loadMenuSections(menuRestaurant);
        }}
      />

      {/* ── Menu Management Panel (slide-in drawer) ── */}
      {showMenuPanel && (
        <div className="mnu-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleCloseMenuPanel(); }}>
          <div className="mnu-drawer">

            {/* Header */}
            <div className="mnu-header">
              <img
                src={menuRestaurant?.imageUrl || restaurantPlaceholderImage}
                alt={menuRestaurant?.name}
                className="mnu-header-logo"
                onError={(e) => (e.target.src = restaurantPlaceholderImage)}
              />
              <div className="mnu-header-info">
                <div className="mnu-header-title">{menuRestaurant?.name}</div>
                <div className="mnu-header-sub">
                  <FaListUl style={{ marginRight: 5 }} />
                  {canUpdateRestaurants || canCreateRestaurants || canDeleteRestaurants
                    ? "Menu Management"
                    : "Menu"}{" "}
                  — {menuSections.length} section{menuSections.length !== 1 ? "s" : ""}&nbsp;·&nbsp;
                  {menuSections.reduce((n, s) => n + (s.products?.length || 0), 0)} product{menuSections.reduce((n, s) => n + (s.products?.length || 0), 0) !== 1 ? "s" : ""}
                </div>
              </div>
              <button className="mnu-close-btn" onClick={handleCloseMenuPanel} title="Close">
                <FaTimes size={14} />
              </button>
            </div>

            {/* Publish state. The whole point of a draft is that you can tell at
                a glance it is not live, so this sits above everything else. */}
            {canUpdateRestaurants && (
              <div
                className={`mnu-publish-bar${
                  menuRestaurant?.publishStatus === "DRAFT" ? " is-draft" : " is-live"
                }`}
              >
                <span className="mnu-publish-icon">
                  {menuRestaurant?.publishStatus === "DRAFT" ? <FaEyeSlash /> : <FaEye />}
                </span>
                <div className="mnu-publish-copy">
                  <strong>
                    {menuRestaurant?.publishStatus === "DRAFT"
                      ? "Draft, not visible to customers"
                      : "Live for customers"}
                  </strong>
                  <small>
                    {menuRestaurant?.publishStatus === "DRAFT"
                      ? "Build the menu and add photos here. Nobody can see this kitchen, its storefront or its menu until you publish it."
                      : "This kitchen, its storefront and its menu are on lusuku.ug and in the apps right now."}
                  </small>
                </div>
                <button
                  type="button"
                  className="mnu-publish-btn"
                  onClick={handleTogglePublish}
                  disabled={publishSaving}
                >
                  {publishSaving
                    ? "Saving…"
                    : menuRestaurant?.publishStatus === "DRAFT"
                    ? "Publish"
                    : "Unpublish"}
                </button>
              </div>
            )}

            {/* Toolbar */}
            <div className="mnu-toolbar">
              <div className="mnu-search-wrap">
                <FaSearch className="mnu-search-icon" />
                <input
                  className="mnu-search-input"
                  type="text"
                  placeholder="Search products…"
                  value={menuSearchQuery}
                  onChange={(e) => setMenuSearchQuery(e.target.value)}
                />
              </div>
              <button
                className="mnu-add-section-btn"
                onClick={() => { setShowAddSectionForm((p) => !p); setEditingSection(null); }}
                disabled={!canCreateRestaurants}
              >
                <FaPlus style={{ fontSize: 10 }} />
                {showAddSectionForm ? "Cancel" : "Add Section"}
              </button>
              <button
                className="mnu-add-section-btn"
                style={{ background: "#2c6fbb" }}
                onClick={() => setShowMenuImport(true)}
                disabled={!canCreateRestaurants}
                title="Bulk upload a filled menu workbook"
              >
                <FaFileImport style={{ fontSize: 10 }} />
                Import Menu
              </button>
              {(() => {
                const photoless = menuSections.reduce(
                  (n, sec) =>
                    n + (sec.products || []).filter((p) => !p.image).length,
                  0
                );
                if (!photoless) return null;
                return (
                  <button
                    type="button"
                    className={`mnu-photoless-btn${menuPhotolessOnly ? " is-active" : ""}`}
                    onClick={() => setMenuPhotolessOnly((prev) => !prev)}
                    title={
                      menuPhotolessOnly
                        ? "Show every product again"
                        : "Show only the products still waiting for a photo"
                    }
                  >
                    <FaImage style={{ fontSize: 10 }} />
                    {photoless} no photo
                  </button>
                );
              })()}
              <label
                className={`mnu-live-only${menuLiveOnly ? " is-on" : ""}`}
                title={
                  menuLiveOnly
                    ? `Showing only dishes customers can see. Switch off to also show the ${inactiveMenuProductCount} hidden ${inactiveMenuProductCount === 1 ? "dish" : "dishes"}.`
                    : "Showing every dish, including the ones hidden from customers."
                }
              >
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={menuLiveOnly}
                  checked={menuLiveOnly}
                  onChange={(e) => setMenuLiveOnly(e.target.checked)}
                />
                <FaToggleOn
                  aria-hidden="true"
                  style={{ fontSize: 18, transform: menuLiveOnly ? "none" : "scaleX(-1)" }}
                />
                {menuLiveOnly ? "Live only" : "All dishes"}
                {inactiveMenuProductCount > 0 ? (
                  <span className="mnu-live-only-count">
                    {menuLiveOnly ? `${inactiveMenuProductCount} hidden` : `${inactiveMenuProductCount} inactive`}
                  </span>
                ) : null}
              </label>
              {visibleMenuProducts.length > 0 && canSelectMenuItems && (
                <label className="mnu-select-visible" title="Select all products currently shown by the filters">
                  <input
                    type="checkbox"
                    checked={allVisibleProductsSelected}
                    onChange={toggleVisibleProductSelection}
                  />
                  Select visible
                </label>
              )}
              {selectedMenuProductIds.length > 0 && canUpdateRestaurants && (
                <>
                  <button
                    type="button"
                    className="mnu-bulk-status-btn is-on"
                    onClick={() => setMenuProductsEnabled(selectedMenuProductIds, true)}
                    disabled={selectedMenuProductIds.some((id) => statusSaving[id])}
                    title="Make the selected dishes visible to customers"
                  >
                    <FaEye style={{ fontSize: 10 }} />
                    Activate selected ({selectedMenuProductIds.length})
                  </button>
                  <button
                    type="button"
                    className="mnu-bulk-status-btn is-off"
                    onClick={() => setMenuProductsEnabled(selectedMenuProductIds, false)}
                    disabled={selectedMenuProductIds.some((id) => statusSaving[id])}
                    title="Hide the selected dishes from customers"
                  >
                    <FaEyeSlash style={{ fontSize: 10 }} />
                    Deactivate selected ({selectedMenuProductIds.length})
                  </button>
                </>
              )}
              {selectedMenuTargets.length > 0 && canSelectMenuItems && (
                <>
                  {canBulkDeleteMenu && (
                    <button
                      type="button"
                      className="mnu-bulk-delete-btn"
                      onClick={() => openMenuDelete({ type: "bulk", items: selectedMenuTargets })}
                    >
                      <FaTrash style={{ fontSize: 10 }} />
                      Delete selected ({selectedMenuTargets.length})
                    </button>
                  )}
                  <button
                    type="button"
                    className="mnu-clear-selection"
                    onClick={() => setMenuSelectedItems({})}
                  >
                    Clear
                  </button>
                </>
              )}
              <div className="mnu-stats">
                <span className="mnu-stat-pill">
                  <FaListUl style={{ fontSize: 9 }} />
                  {menuSections.length} sections
                </span>
                <span className="mnu-stat-pill">
                  <FaTag style={{ fontSize: 9 }} />
                  {menuSections.reduce((n, s) => n + (s.products?.length || 0), 0)} products
                </span>
              </div>
            </div>

            {/* Add Section inline form */}
            {showAddSectionForm && (
              <div className="mnu-add-section-form">
                <form onSubmit={handleAddSection}>
                  <input
                    type="text"
                    placeholder="Section title (e.g. Starters, Main Course) *"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    autoFocus
                    required
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newSectionDesc}
                    onChange={(e) => setNewSectionDesc(e.target.value)}
                  />
                  <div className="mnu-form-row">
                    <button type="submit" className="mnu-form-btn mnu-form-btn-save" disabled={savingSection}>
                      {savingSection ? <Spinner as="span" animation="border" size="sm" /> : <><FaPlus style={{ fontSize: 9 }} /> Create Section</>}
                    </button>
                    <button type="button" className="mnu-form-btn mnu-form-btn-cancel" onClick={() => setShowAddSectionForm(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Body */}
            <div className="mnu-body">
              {menuLoading ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                  <Spinner animation="border" variant="primary" />
                  <p style={{ marginTop: 12, color: "#9aacbf", fontSize: "0.85rem" }}>Loading menu…</p>
                </div>
              ) : menuSections.length === 0 ? (
                <div className="mnu-empty">
                  <div className="mnu-empty-icon"><FaBoxOpen /></div>
                  <p style={{ fontWeight: 700, color: "#6b7c93" }}>No menu sections yet</p>
                  <p style={{ fontSize: "0.82rem" }}>Click "Add Section" to create the first menu section for this restaurant.</p>
                </div>
              ) : (
                (() => {
                  const anyShown = menuSections.some((section) =>
                    (section.products || []).some(menuProductMatches)
                  );
                  if (menuFiltersActive && !anyShown) {
                    return (
                      <div className="mnu-empty">
                        <div className="mnu-empty-icon"><FaSearch /></div>
                        <p style={{ fontWeight: 700, color: "#6b7c93" }}>
                          {menuLiveOnly && !menuSearchQuery.trim() && !menuPhotolessOnly
                            ? "No dishes are live for customers yet"
                            : "No dishes match these filters"}
                        </p>
                        <p style={{ fontSize: "0.82rem" }}>
                          {menuLiveOnly && inactiveMenuProductCount > 0 ? (
                            <>
                              {inactiveMenuProductCount} {inactiveMenuProductCount === 1 ? "dish is" : "dishes are"} hidden from customers.{" "}
                              <button
                                type="button"
                                className="mnu-link-btn"
                                onClick={() => setMenuLiveOnly(false)}
                              >
                                Show all dishes
                              </button>
                            </>
                          ) : (
                            "Clear the search or the photo filter to see more."
                          )}
                        </p>
                      </div>
                    );
                  }
                  return menuSections.map((section) => {
                    const filteredProducts = (section.products || []).filter(menuProductMatches);
                    if (menuFiltersActive && filteredProducts.length === 0) return null;
                    const isExpanded = menuExpandedSections[section._id] !== false;

                    return (
                      <div key={section._id} className="mnu-section">

                        {/* Section header */}
                        <div className="mnu-section-header">
                          {canSelectMenuItems && (
                            <input
                              className="mnu-item-checkbox"
                              type="checkbox"
                              checked={Boolean(menuSelectedItems[`section:${section._id}`])}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() =>
                                toggleMenuSelection({
                                  type: "section",
                                  id: section._id,
                                  name: section.title,
                                  productCount: section.products?.length || 0,
                                })
                              }
                              aria-label={`Select section ${section.title}`}
                              title="Select this section and all of its products"
                            />
                          )}
                          <span
                            className={`mnu-section-chevron${isExpanded ? " open" : ""}`}
                            onClick={() => toggleSectionExpand(section._id)}
                          >
                            <FaChevronRight />
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }} onClick={() => toggleSectionExpand(section._id)}>
                            {editingSection === section._id ? (
                              <span className="mnu-section-name" style={{ color: "#c8880a" }}>Editing…</span>
                            ) : (
                              <span className="mnu-section-name">{section.title}</span>
                            )}
                          </div>
                          <span className="mnu-section-count">
                            <FaTag style={{ fontSize: 8 }} />
                            {section.products?.length || 0}
                          </span>
                          <div className="mnu-section-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="mnu-sec-btn mnu-sec-btn-add"
                              onClick={() => handleAddProductToSection(section._id)}
                              disabled={!canCreateRestaurants}
                              title="Add product to this section"
                            >
                              <FaPlus style={{ fontSize: 9 }} /> Product
                            </button>
                            <button
                              className="mnu-sec-btn mnu-sec-btn-edit"
                              onClick={() => {
                                if (editingSection === section._id) {
                                  setEditingSection(null);
                                } else {
                                  handleStartEditSection(section);
                                }
                              }}
                              disabled={!canUpdateRestaurants}
                              title="Edit section"
                            >
                              <FaEdit style={{ fontSize: 9 }} />
                            </button>
                            <button
                              className="mnu-sec-btn mnu-sec-btn-del"
                              onClick={() => {
                                openMenuDelete({
                                  type: "section",
                                  id: section._id,
                                  name: section.title,
                                  productCount: section.products?.length || 0,
                                });
                              }}
                              disabled={!canDeleteRestaurants}
                              title="Delete section (and all its products)"
                            >
                              <FaTrash style={{ fontSize: 9 }} />
                            </button>
                          </div>
                        </div>

                        {/* Edit section inline form */}
                        {editingSection === section._id && (
                          <div className="mnu-edit-section-form">
                            <input
                              type="text"
                              value={editSectionTitle}
                              onChange={(e) => setEditSectionTitle(e.target.value)}
                              placeholder="Section title"
                              autoFocus
                            />
                            <input
                              type="text"
                              value={editSectionDesc}
                              onChange={(e) => setEditSectionDesc(e.target.value)}
                              placeholder="Description (optional)"
                            />
                            <div className="mnu-form-row">
                              <button
                                className="mnu-form-btn mnu-form-btn-save"
                                onClick={() => handleSaveEditSection(section._id)}
                                disabled={savingSection}
                              >
                                {savingSection ? <Spinner as="span" animation="border" size="sm" /> : "Save"}
                              </button>
                              <button className="mnu-form-btn mnu-form-btn-cancel" onClick={() => setEditingSection(null)}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Products */}
                        {isExpanded && (
                          <div className="mnu-products">
                            {filteredProducts.length === 0 ? (
                              <div className="mnu-no-products">
                                <FaBoxOpen style={{ marginRight: 5 }} />
                                No products in this section.
                                <button
                                  style={{ marginLeft: 8, background: "none", border: "none", color: "#0e7490", fontWeight: 700, cursor: "pointer", fontSize: "0.78rem" }}
                                  onClick={() => handleAddProductToSection(section._id)}
                                  disabled={!canCreateRestaurants}
                                >
                                  Add one →
                                </button>
                              </div>
                            ) : (
                              <div className="mnu-products-grid">
                                {filteredProducts.map((product) => (
                                  <div
                                    key={product._id}
                                    className={`mnu-product-card${menuSelectedItems[`product:${product._id}`] ? " is-selected" : ""}`}
                                  >
                                    {canSelectMenuItems && (
                                      <input
                                        className="mnu-product-checkbox"
                                        type="checkbox"
                                        checked={Boolean(menuSelectedItems[`product:${product._id}`])}
                                        onChange={() =>
                                          toggleMenuSelection({
                                            type: "product",
                                            id: product._id,
                                            name: product.name,
                                            sectionId: section._id,
                                          })
                                        }
                                        aria-label={`Select product ${product.name}`}
                                        title="Select this dish"
                                      />
                                    )}
                                    {!product.image && (
                                      <span
                                        className="mnu-no-photo-tag"
                                        title="This dish has no photo yet. Edit it to add one."
                                      >
                                        <FaImage /> No photo yet
                                      </span>
                                    )}
                                    <img
                                      src={product.image || menuPlaceholder}
                                      alt={product.name}
                                      className={`mnu-product-img${product.image ? "" : " is-placeholder"}`}
                                      onError={(e) => { e.target.src = menuPlaceholder; e.target.classList.add("is-placeholder"); }}
                                    />
                                    <div className="mnu-product-body">
                                      <div className="mnu-product-name" title={product.name}>{product.name}</div>
                                      <div className="mnu-product-price">
                                        {new Intl.NumberFormat("en-UG", { style: "currency", currency: "UGX" }).format(product.price || 0)}
                                      </div>
                                      {/* The status pill IS the switch: new and imported
                                          dishes start Inactive and QC flips them here once
                                          checked, without opening the Edit form. */}
                                      <button
                                        type="button"
                                        className={`mnu-product-status${canUpdateRestaurants ? " is-toggle" : ""}`}
                                        style={product.enabled !== false
                                          ? { background: "#e8f5e9", color: "#2e7d32" }
                                          : { background: "#fce4ec", color: "#c62828" }
                                        }
                                        disabled={!canUpdateRestaurants || Boolean(statusSaving[product._id])}
                                        onClick={() => setMenuProductsEnabled([product._id], product.enabled === false)}
                                        title={
                                          !canUpdateRestaurants
                                            ? undefined
                                            : product.enabled !== false
                                            ? "Active: customers can see this dish. Click to deactivate."
                                            : "Inactive: hidden from customers. Click to activate once it has been checked."
                                        }
                                      >
                                        {product.enabled !== false ? <FaEye /> : <FaEyeSlash />}
                                        {statusSaving[product._id]
                                          ? "Saving"
                                          : product.enabled !== false ? "Customers can see it" : "Hidden from customers"}
                                        {canUpdateRestaurants && !statusSaving[product._id] && (
                                          <FaToggleOn
                                            style={{ fontSize: 16, marginLeft: "auto", transform: product.enabled !== false ? "none" : "scaleX(-1)" }}
                                          />
                                        )}
                                      </button>
                                      {/* Outlet availability — only shown if restaurant has outlets */}
                                      {menuRestaurant?.outlets?.length > 0 && (
                                        <div style={{ marginTop: 5 }}>
                                          <div style={{ fontSize: "0.68rem", color: "#9aacbf", marginBottom: 3, fontWeight: 600 }}>
                                            <FaStore style={{ fontSize: 8, marginRight: 3 }} />Outlets
                                          </div>
                                          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                                            {menuRestaurant.outlets.map((outlet) => {
                                              const isEnabled = (outlet.menuItems || []).some(
                                                (id) => id?.toString() === product._id?.toString()
                                              );
                                              const toggling = outletToggling[`${outlet._id}_${product._id}`];
                                              return (
                                                <button
                                                  key={outlet._id}
                                                  onClick={() => handleToggleOutletProduct(outlet, product)}
                                                  disabled={!canUpdateRestaurants || toggling}
                                                  title={isEnabled ? `Remove from ${outlet.name}` : `Add to ${outlet.name}`}
                                                  style={{
                                                    fontSize: "0.66rem",
                                                    padding: "2px 6px",
                                                    borderRadius: 10,
                                                    border: `1px solid ${isEnabled ? "#c8e6c9" : "#e0e0e0"}`,
                                                    background: isEnabled ? "#e8f5e9" : "#f5f5f5",
                                                    color: isEnabled ? "#2e7d32" : "#9e9e9e",
                                                    cursor: toggling ? "wait" : "pointer",
                                                    fontWeight: 600,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 3,
                                                  }}
                                                >
                                                  {toggling ? (
                                                    <Spinner as="span" animation="border" size="sm" style={{ width: 8, height: 8 }} />
                                                  ) : (
                                                    <FaCircle style={{ fontSize: 5, color: isEnabled ? "#4caf50" : "#bdbdbd" }} />
                                                  )}
                                                  {outlet.name}
                                                </button>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                      <div className="mnu-product-actions">
                                        <button
                                          className="mnu-prod-btn mnu-prod-btn-edit"
                                          onClick={() => handleEditProduct(product._id)}
                                          disabled={!canUpdateRestaurants}
                                          title="Edit product"
                                        >
                                          <FaEdit /> Edit
                                        </button>
                                        <button
                                          className="mnu-prod-btn mnu-prod-btn-del"
                                          onClick={() => {
                                            openMenuDelete({
                                              type: "product",
                                              id: product._id,
                                              name: product.name,
                                              sectionId: section._id,
                                            });
                                          }}
                                          title="Delete product"
                                          disabled={!canDeleteRestaurants}
                                        >
                                          <FaTrash /> Delete
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Menu Delete Confirmation Modal ── */}
      <Modal show={showMenuDeleteModal} onHide={closeMenuDeleteModal} centered size="sm">
        <Modal.Header closeButton style={{ borderBottom: "2px solid #dc3545", background: "#fafbfc" }}>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaTrash style={{ color: "#dc3545" }} />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "18px 20px" }}>
          <div style={{ textAlign: "center" }}>
            <FaExclamationTriangle style={{ fontSize: "2rem", color: "#dc3545", marginBottom: 10 }} />
            <p style={{ fontSize: "0.88rem", marginBottom: 8 }}>
              {modalDeleteTargets.length === 1 ? (
                <>
                  Are you sure you want to delete{" "}
                  <strong style={{ color: modalSectionTargets.length ? "#c8880a" : "#1a2a3a" }}>
                    "{modalDeleteTargets[0]?.name}"
                  </strong>?
                </>
              ) : (
                <>
                  Are you sure you want to delete{" "}
                  <strong>{modalDeleteTargets.length} selected menu items</strong>?
                </>
              )}
            </p>
            {modalSectionTargets.length > 0 && (
              <p style={{ fontSize: "0.78rem", color: "#c62828", background: "#fce4ec", borderRadius: 7, padding: "8px 12px" }}>
                {modalSectionTargets.length === 1
                  ? `Deleting this section will also delete all ${modalSectionTargets[0].productCount || 0} products inside it.`
                  : `Deleting these ${modalSectionTargets.length} sections will also delete all products inside them.`}
              </p>
            )}
            {modalDeleteTargets.length > 1 && (
              <p style={{ fontSize: "0.76rem", color: "#475569", marginBottom: 8 }}>
                {modalSectionTargets.length} section{modalSectionTargets.length === 1 ? "" : "s"} and{" "}
                {modalAffectedProductCount} product{modalAffectedProductCount === 1 ? "" : "s"} will be removed.
              </p>
            )}
            <p style={{ fontSize: "0.75rem", color: "#9aacbf" }}>This action cannot be undone.</p>
          </div>
          <div style={{ marginTop: 14 }}>
            <label htmlFor="menu-delete-reason" style={{ display: "block", fontSize: "0.76rem", fontWeight: 700, color: "#475569", marginBottom: 5 }}>
              Reason for deletion <span style={{ color: "#dc3545" }}>*</span>
            </label>
            <textarea
              id="menu-delete-reason"
              rows={2}
              value={menuDeleteReason}
              onChange={(event) => setMenuDeleteReason(event.target.value)}
              placeholder="e.g. Duplicate item or no longer sold"
              maxLength={300}
              autoFocus
              style={{ width: "100%", resize: "vertical", border: "1.5px solid #cbd5e1", borderRadius: 7, padding: "8px 10px", fontSize: "0.8rem", outline: "none" }}
            />
            <small style={{ color: menuDeleteReason.trim().length > 0 && menuDeleteReason.trim().length < 3 ? "#dc3545" : "#94a3b8", fontSize: "0.68rem" }}>
              Required for the audit trail (minimum 3 characters).
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f0f2f5" }}>
          <button
            className="rst-modal-footer-btn"
            onClick={closeMenuDeleteModal}
            disabled={menuDeleteLoading}
            style={{ background: "#f4f6f9", color: "#6b7c93", border: "1.5px solid #e4e8ef" }}
          >
            Cancel
          </button>
          <button
            className="rst-modal-footer-btn"
            onClick={handleMenuDeleteConfirm}
            disabled={menuDeleteLoading || menuDeleteReason.trim().length < 3}
            style={{ background: "#dc3545", color: "#fff", opacity: menuDeleteLoading || menuDeleteReason.trim().length < 3 ? 0.55 : 1 }}
          >
            {menuDeleteLoading
              ? <><Spinner as="span" animation="border" size="sm" style={{ color: "#fff" }} />&nbsp;Deleting…</>
              : <><FaTrash style={{ fontSize: 10 }} /> Delete {modalDeleteTargets.length > 1 ? modalDeleteTargets.length : ""}</>
            }
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── AI Commerce Toggle Confirmation Modal ── */}
      <Modal
        show={showAIModal}
        onHide={() => { setShowAIModal(false); setAiModalTarget(null); }}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {aiModalTarget?.currentValue ? 'Disable AI Commerce' : 'Enable AI Commerce'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px 24px' }}>
          <p style={{ fontSize: '0.88rem', marginBottom: 10 }}>
            {aiModalTarget?.currentValue
              ? <>AI agents will <strong>no longer</strong> be able to search or order from <strong>{aiModalTarget?.name}</strong>.</>
              : <><strong>{aiModalTarget?.name}</strong> and its products will become discoverable and orderable by AI agents (ChatGPT, Gemini, etc.).</>}
          </p>
          <div style={{
            background: aiModalTarget?.currentValue ? '#fff3cd' : '#e8f5e9',
            border: `1px solid ${aiModalTarget?.currentValue ? '#ffc107' : '#c8e6c9'}`,
            borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem',
            color: aiModalTarget?.currentValue ? '#856404' : '#2e7d32',
          }}>
            {aiModalTarget?.currentValue
              ? '⚠️ This will immediately remove the vendor from the AI Commerce Gateway.'
              : '✅ This will immediately expose the vendor to the AI Commerce Gateway.'}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f0f2f5', gap: 8 }}>
          <button
            className="rst-modal-footer-btn"
            onClick={() => { setShowAIModal(false); setAiModalTarget(null); }}
            style={{ background: '#f4f6f9', color: '#6b7c93', border: '1.5px solid #e4e8ef' }}
          >
            Cancel
          </button>
          <button
            className="rst-modal-footer-btn"
            onClick={confirmToggleAI}
            style={{ background: aiModalTarget?.currentValue ? '#dc3545' : '#117a44', color: '#fff' }}
          >
            {aiModalTarget?.currentValue ? 'Disable AI' : 'Enable AI'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Manage Business Hours Modal ── */}
      <Modal show={showHoursModal} onHide={() => setShowHoursModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaClock style={{ color: "#083860" }} />
            Manage Business Hours: {selectedRestaurantForHours?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          {selectedRestaurantForHours && (
            <div>
              <div style={{ background: "#e8f5e9", border: "1.5px solid #c8e6c9", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: "0.85rem" }}>
                <strong style={{ color: "#2e7d32" }}>📅 Restaurant Operating Hours</strong>
                <p style={{ margin: "6px 0 0", color: "#4a5568" }}>
                  Set when <strong>{selectedRestaurantForHours.name}</strong> is open for orders. Outlets without custom hours inherit these settings.
                </p>
              </div>
              {isLoadingHours ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <Spinner animation="border" variant="success" />
                  <p style={{ marginTop: 10, color: "#9aacbf", fontSize: "0.85rem" }}>Loading business hours…</p>
                </div>
              ) : (
                <OpenHoursManager
                  vendorType="restaurant"
                  vendorId={selectedRestaurantForHours?._id}
                  openHours={restaurantHours}
                  timezone={selectedRestaurantForHours?.timezone || "Africa/Kampala"}
                  onSave={handleSaveRestaurantHours}
                  isLoading={isSavingHours}
                  onValidationError={() => toast.error("Please fix validation errors before saving")}
                />
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Restaurants;
